import { l as lodash } from './lodash-5c867657.js';
import './ajv-5cae5b23.js';
import './muuri.module-ee0c2f7a.js';
import './smoothscroll-9c638e2b.js';
import './mo-utils-eb97378b.js';
import './index-9b297a6c.js';
import { D as DataStoreMockPlugin } from './DataStoreMockPlugin-243a33f9.js';
import { M as MoBaseStore, a as MoModel } from './MoBaseStore-6200f34f.js';
import { M as MoDatesService } from './date-service-e05d3c96.js';
import { v as v4 } from './v4-551b60de.js';
import { a as getSortFieldMap, b as getSortOrderMap } from './sort-helper-9d77b6e1.js';

const etagCache = new Map();
const saveEtagValue = (response) => {
    if (response && response.headers) {
        const headers = response.headers;
        const url = response.url;
        const etagValue = headers.get('ETag');
        if (etagValue) {
            etagCache.set(url, etagValue);
        }
    }
};
const applyETag = (url, requestObj) => {
    const { headers, eTag, method } = requestObj;
    const { setHeader } = eTag;
    if (eTag === null || eTag === void 0 ? void 0 : eTag.enabled) {
        const etagValue = etagCache.get(url);
        if (etagValue) {
            setHeader(etagValue, headers, method);
        }
    }
};

const makeFetchRequest = (url, request = {}) => {
    const requestObj = Object.assign({ credentials: 'include', method: 'GET' }, request);
    requestObj.headers = lodash.defaults(requestObj.headers || {}, {
        'Accept': 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
    });
    return window.fetch(url, requestObj)
        .then((response) => {
        var _a;
        const success = response.ok;
        let resultPromise = response.text().then((responseText) => {
            try {
                let result = JSON.parse(responseText === '' ? '{}' : responseText);
                if (!success) {
                    result = Object.assign(Object.assign({}, result), lodash.pick(response, 'status', 'statusText'));
                }
                return result;
            }
            catch (error) {
                return responseText;
            }
        });
        if (success) {
            if ((_a = request.eTag) === null || _a === void 0 ? void 0 : _a.enabled) {
                saveEtagValue(response);
            }
        }
        else {
            resultPromise = resultPromise.then(Promise.reject.bind(Promise));
        }
        return resultPromise;
    });
};

const buildIntrospectApi = (options) => {
    const serviceName = lodash.camelCase(options.serviceName);
    const serviceConfig = lodash.find(options.apiMeta, (_meta, service) => lodash.camelCase(service.replace(/::.*$/, '')) === serviceName);
    return serviceConfig && lodash(serviceConfig.methods)
        .keyBy('name')
        .mapValues((methodMeta) => (requestOptions) => callMethod(serviceName, methodMeta, requestOptions, options))
        .value() || null;
};
const callMethod = (serviceName, methodMeta, requestOptions, apiServiceOptions) => {
    // Removed queryParams from default config as fetch doesn't support QPs as an object :'( REF: https://github.com/github/fetch/issues/256
    const defaultConfig = { pathParams: {}, headers: {}, data: '' };
    const request = lodash.assign(defaultConfig, lodash.cloneDeep(requestOptions));
    const tpl = methodMeta.uri.replace(/{/g, '${').replace(/: ?\.\*/, '');
    const basePath = apiServiceOptions.getBasePath(requestOptions, serviceName, methodMeta.name);
    const queryParams = lodash.get(request, 'queryParams');
    const eTagConfig = requestOptions.eTag;
    let url;
    request.method = methodMeta.verb;
    request.headers = Object.assign(Object.assign({}, apiServiceOptions.getRequestHeaders(requestOptions, serviceName, methodMeta.name)), requestOptions.headers);
    if (request.data) {
        request.body = JSON.stringify(request.data);
    }
    try {
        const entityUriTemplate = lodash.template(tpl)(request.pathParams);
        url = basePath + entityUriTemplate;
        if (queryParams) {
            const urlQueryParams = lodash.map(queryParams, (val, key) => {
                return lodash.flatten([val]).map((value) => `${key}=${value}`).join('&');
            }).join('&');
            url = `${url}?${urlQueryParams}`;
        }
        if (eTagConfig === null || eTagConfig === void 0 ? void 0 : eTagConfig.enabled) {
            applyETag(url, request);
        }
    }
    catch (error) {
        console.error(`Invoking API "${serviceName}.${methodMeta.name}" with missing path params: ${error.message}. Request config: `, requestOptions);
        throw error;
    }
    return makeFetchRequest(url, request)
        .catch((error) => {
        lodash.invoke(apiServiceOptions, 'onError', error);
        throw error;
    });
};

/** Service for communicating with endpoints based on the configurable REST APIs Specifications. */
class ApiService {
    static setDefaultConfig(config) {
        ApiService.defaultConfig = lodash.merge(ApiService.defaultConfig, config);
    }
    constructor(config) {
        this.config = {
            localPort: '9001',
            prodPort: '6764',
            apiPath: '/api',
            apiMeta: {},
            requestProps: {},
            buildApi: buildIntrospectApi,
            onError: lodash.bind(this.onError, this),
            getServiceHost: lodash.bind(this.getServiceHost, this),
            getRequestHeaders: lodash.bind(this.getRequestHeaders, this),
            checkAuthState: lodash.bind(this.checkAuthState, this),
            checkAuthStateOnError: lodash.bind(this.checkAuthStateOnError, this),
        };
        this.setConfig(lodash.merge(ApiService.defaultConfig, config));
    }
    /**
     * Provides the snapshot of current service states.
     */
    get meta() {
        const propList = ['config', 'serviceHost', 'basePath', 'api'];
        const props = lodash(this).pick(propList).mapValues(lodash.cloneDeep).value();
        return Object.assign({}, props);
    }
    setConfig(config, overwrite = false) {
        this.config = overwrite ? config : lodash.merge(this.config, config);
        this.serviceHost = this.config.getServiceHost();
        this.basePath = `${this.serviceHost}${this.config.apiPath}`;
    }
    addApi(apiMeta) {
        this.setConfig(lodash.merge(this.config, { apiMeta }));
    }
    /**
     * Invoke API with service name, service method, and config such as:
     *
     * this.invoke(serviceName, serviceMethod, {
     *   pathParams: { url-param1: value, url-param2: value, ... },
     *   data: dataBody,
     *   queryParams: { query-params },
     *   headers: { headers }
     * });
     */
    invoke(serviceName, serviceMethod, config) {
        const mergedConfig = lodash.merge(config, this.config.requestProps);
        this.config.checkAuthState();
        return this.invokeService(serviceName, serviceMethod, mergedConfig);
    }
    makeRequest(urlPath, fetchOption = {}) {
        return makeFetchRequest(`${this.basePath}/${urlPath}`, fetchOption)
            .catch(this.onError);
    }
    getBasePath(_config = {}, _serviceName, _methodName) {
        return this.basePath;
    }
    getRequestHeaders(_config = {}, _serviceName, _methodName) {
        return {};
    }
    getServiceHost() {
        const isRunningLocalDev = lodash.get(window, 'location.port') === String(this.config.localPort);
        return isRunningLocalDev
            ? `http://${window.location.hostname}:${this.config.prodPort}`
            : `${window.location.protocol}//${window.location.hostname}:${window.location.port}`;
    }
    invokeService(serviceName, method, ...args) {
        const apiOptions = {
            serviceName,
            apiMeta: this.config.apiMeta,
            getBasePath: lodash.bind(this.getBasePath, this),
            getRequestHeaders: lodash.bind(this.getRequestHeaders, this),
            onError: lodash.bind(this.onError, this),
        };
        const api = this.config.buildApi(apiOptions);
        if (api && api[method]) {
            return api[method](...args);
        }
        else if (!api) {
            throw new Error(`Invalid service: ${serviceName}`);
        }
        else {
            throw new Error(`Invalid service method: ${serviceName}.${method}`);
        }
    }
    /**
     * Handle common error cases.
     */
    onError(error) {
        if (this.config.checkAuthStateOnError(error)) {
            throw error;
        }
    }
    /**
     * The pre-request hook to check the authentication state from cookies and redirect to auth flow if necessary.
     * @returns {? Promise<boolean>} True if it's authenticated (optional).
     */
    async checkAuthState() {
        return true;
    }
    /**
     * The post-response hook to check the authentication state on Error response and redirect to auth flow if necessary.
     * @returns {boolean} True if it's authenticated.
     */
    checkAuthStateOnError(_error) {
        return true;
    }
}

/* global setTimeout, clearTimeout */

var dist = function debounce(fn) {
  var wait = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

  var lastCallAt = void 0;
  var deferred = void 0;
  var timer = void 0;
  var pendingArgs = [];
  return function debounced() {
    var currentWait = getWait(wait);
    var currentTime = new Date().getTime();

    var isCold = !lastCallAt || currentTime - lastCallAt > currentWait;

    lastCallAt = currentTime;

    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (isCold && options.leading) {
      return options.accumulate ? Promise.resolve(fn.call(this, [args])).then(function (result) {
        return result[0];
      }) : Promise.resolve(fn.call.apply(fn, [this].concat(args)));
    }

    if (deferred) {
      clearTimeout(timer);
    } else {
      deferred = defer();
    }

    pendingArgs.push(args);
    timer = setTimeout(flush.bind(this), currentWait);

    if (options.accumulate) {
      var argsIndex = pendingArgs.length - 1;
      return deferred.promise.then(function (results) {
        return results[argsIndex];
      });
    }

    return deferred.promise;
  };

  function flush() {
    var thisDeferred = deferred;
    clearTimeout(timer);

    Promise.resolve(options.accumulate ? fn.call(this, pendingArgs) : fn.apply(this, pendingArgs[pendingArgs.length - 1])).then(thisDeferred.resolve, thisDeferred.reject);

    pendingArgs = [];
    deferred = null;
  }
};

function getWait(wait) {
  return typeof wait === 'function' ? wait() : wait;
}

function defer() {
  var deferred = {};
  deferred.promise = new Promise(function (resolve, reject) {
    deferred.resolve = resolve;
    deferred.reject = reject;
  });
  return deferred;
}

let localApiService;
let apiServiceConfig = {};
const setStoreApiService = (apiService) => {
    localApiService = apiService;
};
const getStoreApiService = (apiServiceClass = ApiService) => {
    localApiService !== null && localApiService !== void 0 ? localApiService : (localApiService = new apiServiceClass(apiServiceConfig));
    return localApiService;
};
const setStoreApiServiceConfig = (config) => {
    apiServiceConfig = lodash.merge(apiServiceConfig, config);
    if (localApiService) {
        localApiService.setConfig(config);
    }
};
const invokeService = (apiConfig, options = {}, defaultParams = {}) => {
    if (lodash.isFunction(apiConfig === null || apiConfig === void 0 ? void 0 : apiConfig.doBefore)) {
        apiConfig.doBefore(apiConfig);
    }
    return doInvokeService(apiConfig, options, defaultParams);
};
const doInvokeService = (apiConfig, options = {}, defaultParams = {}) => {
    const params = getParams(apiConfig, options, defaultParams);
    const apiService = getStoreApiService();
    return apiService.invoke(apiConfig.service, apiConfig.name, params);
};
/**
 * Resolve request params to apiService based on config and passed options.
 *
 * @private
 */
const getParams = (apiConfig, options = {}, defaultParams = {}) => {
    var _a;
    const paramsList = 'pathParams, queryParams, headers'.split(', ');
    const params = lodash.cloneDeep(apiConfig);
    const requestData = options.data || apiConfig.data;
    const extractTokenExpressionRegexp = /^\${(.*?)}$/;
    const storeContext = apiConfig.storeContext;
    lodash.defaults(options, {
        itemData: defaultParams.data || {}
    });
    if (requestData) {
        params.data = requestData;
    }
    if (storeContext) {
        params.eTag = (_a = storeContext.config) === null || _a === void 0 ? void 0 : _a.eTag;
    }
    lodash.each(lodash.pick(params, paramsList), (paramGroup) => {
        lodash.each(paramGroup, (value, key, property) => {
            if (lodash.isString(value)) {
                const [, tokenPath] = value.match(extractTokenExpressionRegexp) || [];
                if (lodash.isString(tokenPath)) {
                    property[key] = lodash.get(options, tokenPath.trim());
                }
            }
        });
    });
    return lodash.merge(params, defaultParams);
};

const getOrCreateSession = () => {
    let sessionId = sessionStorage.getItem('sbSessionId');
    if (!sessionId) {
        sessionId = `${v4()}`;
        sessionStorage.setItem('sbSessionId', sessionId);
    }
    return sessionId;
};
const getVisitorId = () => {
    let userId = localStorage.getItem('sbVisitorId');
    if (!userId) {
        userId = `${v4()}`;
        localStorage.setItem('sbVisitorId', userId);
    }
    return userId;
};

const storeHelpers = /*#__PURE__*/Object.freeze({
    __proto__: null,
    getOrCreateSession: getOrCreateSession,
    getVisitorId: getVisitorId
});

const buildTerm = (term, count, prevCount) => ({ term, count_main: count, count_previous: prevCount });
const mockSearchTrendsDataHourly = {
    delta_pct: 281.25,
    series: {
        main: [
            {
                time: '2021-09-25 10:01:00',
                value: {
                    int: 22,
                },
            },
            {
                time: '2021-09-25 10:04:00',
                value: {
                    int: 28,
                },
            },
            {
                time: '2021-09-25 10:09:00',
                value: {
                    int: 47,
                },
            },
            {
                time: '2021-09-25 10:10:00',
                value: {
                    int: 103,
                },
            },
            {
                time: '2021-09-25 10:11:00',
                value: {
                    int: 139,
                },
            },
            {
                time: '2021-09-25 10:16:00',
                value: {
                    int: 24,
                },
            },
            {
                time: '2021-09-25 10:17:00',
                value: {
                    int: 66,
                },
            },
            {
                time: '2021-09-25 10:22:00',
                value: {
                    int: 99,
                },
            },
            {
                time: '2021-09-25 10:29:00',
                value: {
                    int: 77,
                },
            },
            {
                time: '2021-09-25 10:33:00',
                value: {
                    int: 81,
                },
            },
            {
                time: '2021-09-25 10:36:00',
                value: {
                    int: 64,
                },
            },
            {
                time: '2021-09-25 10:37:00',
                value: {
                    int: 61,
                },
            },
            {
                time: '2021-09-25 10:40:00',
                value: {
                    int: 52,
                },
            },
            {
                time: '2021-09-25 10:43:00',
                value: {
                    int: 26,
                },
            },
            {
                time: '2021-09-25 10:44:00',
                value: {
                    int: 37,
                },
            },
            {
                time: '2021-09-25 10:46:00',
                value: {
                    int: 41,
                },
            },
            {
                time: '2021-09-25 10:48:00',
                value: {
                    int: 57,
                },
            },
            {
                time: '2021-09-25 10:51:00',
                value: {
                    int: 62,
                },
            },
            {
                time: '2021-09-25 10:55:00',
                value: {
                    int: 63,
                },
            },
            {
                time: '2021-09-25 10:58:00',
                value: {
                    int: 66,
                },
            },
            {
                time: '2021-09-25 10:59:00',
                value: {
                    int: 73,
                },
            },
        ],
        previous: [
            {
                time: '2021-09-25 09:03:00',
                value: {
                    int: 12,
                },
            },
            {
                time: '2021-09-25 09:14:00',
                value: {
                    int: 34,
                },
            },
            {
                time: '2021-09-25 09:15:00',
                value: {
                    int: 41,
                },
            },
            {
                time: '2021-09-25 09:20:00',
                value: {
                    int: 17,
                },
            },
            {
                time: '2021-09-25 09:26:00',
                value: {
                    int: 11,
                },
            },
            {
                time: '2021-09-25 09:31:00',
                value: {
                    int: 49,
                },
            },
            {
                time: '2021-09-25 09:32:00',
                value: {
                    int: 38,
                },
            },
            {
                time: '2021-09-25 09:34:00',
                value: {
                    int: 42,
                },
            },
            {
                time: '2021-09-25 09:40:00',
                value: {
                    int: 33,
                },
            },
            {
                time: '2021-09-25 09:43:00',
                value: {
                    int: 39,
                },
            },
            {
                time: '2021-09-25 09:44:00',
                value: {
                    int: 35,
                },
            },
            {
                time: '2021-09-25 09:45:00',
                value: {
                    int: 33,
                },
            },
            {
                time: '2021-09-25 09:54:00',
                value: {
                    int: 28,
                },
            },
            {
                time: '2021-09-25 09:55:00',
                value: {
                    int: 24,
                },
            },
            {
                time: '2021-09-25 09:59:00',
                value: {
                    int: 20,
                },
            },
        ],
    },
    total: {
        int: 612,
    },
};
const mockSearchTrendsDataWeekly = {
    delta_pct: 91.66666666666666,
    series: {
        main: [
            {
                time: '2021-10-07 23:00:00',
                value: {
                    int: 212,
                },
            },
            {
                time: '2021-10-07 18:00:00',
                value: {
                    int: 203,
                },
            },
            {
                time: '2021-10-07 14:00:00',
                value: {
                    int: 188,
                },
            },
            {
                time: '2021-10-06 12:00:00',
                value: {
                    int: 145,
                },
            },
            {
                time: '2021-10-06 11:00:00',
                value: {
                    int: 122,
                },
            },
            {
                time: '2021-10-05 09:00:00',
                value: {
                    int: 190,
                },
            },
            {
                time: '2021-10-04 08:00:00',
                value: {
                    int: 250,
                },
            },
            {
                time: '2021-10-03 22:00:00',
                value: {
                    int: 28,
                },
            },
            {
                time: '2021-10-03 06:00:00',
                value: {
                    int: 56,
                },
            },
            {
                time: '2021-10-03 02:00:00',
                value: {
                    int: 37,
                },
            },
            {
                time: '2021-10-02 09:00:00',
                value: {
                    int: 86,
                },
            },
            {
                time: '2021-10-01 14:00:00',
                value: {
                    int: 130,
                },
            },
        ],
        previous: [
            {
                time: '2021-09-30 17:00:00',
                value: {
                    int: 160,
                },
            },
            {
                time: '2021-09-30 13:00:00',
                value: {
                    int: 154,
                },
            },
            {
                time: '2021-09-29 21:00:00',
                value: {
                    int: 125,
                },
            },
            {
                time: '2021-09-29 16:00:00',
                value: {
                    int: 120,
                },
            },
            {
                time: '2021-09-29 10:00:00',
                value: {
                    int: 165,
                },
            },
            {
                time: '2021-09-28 22:00:00',
                value: {
                    int: 104,
                },
            },
            {
                time: '2021-09-28 20:00:00',
                value: {
                    int: 99,
                },
            },
            {
                time: '2021-09-27 15:00:00',
                value: {
                    int: 178,
                },
            },
            {
                time: '2021-09-26 18:00:00',
                value: {
                    int: 150,
                },
            },
            {
                time: '2021-09-26 11:00:00',
                value: {
                    int: 132,
                },
            },
            {
                time: '2021-09-26 07:00:00',
                value: {
                    int: 102,
                },
            },
            {
                time: '2021-09-25 19:00:00',
                value: {
                    int: 166,
                },
            },
            {
                time: '2021-09-24 23:00:00',
                value: {
                    int: 132,
                },
            },
            {
                time: '2021-09-24 12:00:00',
                value: {
                    int: 101,
                },
            },
        ],
    },
    total: {
        int: 807,
    },
};
const mockSearchTrendsDataDaily = {
    delta_pct: 298,
    series: {
        main: [
            {
                time: '2021-07-28 02:00:00',
                value: {
                    int: 4,
                },
            },
            {
                time: '2021-07-28 03:00:00',
                value: {
                    int: 4,
                },
            },
            {
                time: '2021-07-28 16:00:00',
                value: {
                    int: 35,
                },
            },
            {
                time: '2021-07-28 17:00:00',
                value: {
                    int: 2,
                },
            },
            {
                time: '2021-07-28 18:00:00',
                value: {
                    int: 2,
                },
            },
            {
                time: '2021-07-28 20:00:00',
                value: {
                    int: 128,
                },
            },
            {
                time: '2021-07-28 21:00:00',
                value: {
                    int: 115,
                },
            },
            {
                time: '2021-07-28 22:00:00',
                value: {
                    int: 125,
                },
            },
            {
                time: '2021-07-28 23:00:00',
                value: {
                    int: 381,
                },
            },
        ],
        previous: [
            {
                time: '2021-07-27 01:00:00',
                value: {
                    int: 46,
                },
            },
            {
                time: '2021-07-27 02:00:00',
                value: {
                    int: 89,
                },
            },
            {
                time: '2021-07-27 04:00:00',
                value: {
                    int: 5,
                },
            },
            {
                time: '2021-07-27 05:00:00',
                value: {
                    int: 7,
                },
            },
            {
                time: '2021-07-27 15:00:00',
                value: {
                    int: 4,
                },
            },
            {
                time: '2021-07-27 17:00:00',
                value: {
                    int: 8,
                },
            },
            {
                time: '2021-07-27 22:00:00',
                value: {
                    int: 35,
                },
            },
            {
                time: '2021-07-27 23:00:00',
                value: {
                    int: 6,
                },
            },
        ],
    },
    total: {
        int: 796,
    },
};
const mockSearchTrendsDataMonthly = {
    delta_pct: 298,
    series: {
        main: [
            {
                time: '2021-07-29 00:00:00',
                value: {
                    int: 47,
                },
            },
            {
                time: '2021-07-30 00:00:00',
                value: {
                    int: 35,
                },
            },
            {
                time: '2021-07-31 00:00:00',
                value: {
                    int: 27,
                },
            },
            {
                time: '2021-08-01 0:00:00',
                value: {
                    int: 32,
                },
            },
            {
                time: '2021-08-02 0:00:00',
                value: {
                    int: 37,
                },
            },
            {
                time: '2021-08-03 00:00:00',
                value: {
                    int: 128,
                },
            },
            {
                time: '2021-08-04 00:00:00',
                value: {
                    int: 99,
                },
            },
            {
                time: '2021-08-05 00:00:00',
                value: {
                    int: 107,
                },
            },
            {
                time: '2021-08-05 00:00:00',
                value: {
                    int: 100,
                },
            },
            {
                time: '2021-08-06 00:00:00',
                value: {
                    int: 104,
                },
            },
            {
                time: '2021-08-07 00:00:00',
                value: {
                    int: 125,
                },
            },
            {
                time: '2021-08-09 00:00:00',
                value: {
                    int: 233,
                },
            },
            {
                time: '2021-08-10 00:00:00',
                value: {
                    int: 142,
                },
            },
            {
                time: '2021-08-11 00:00:00',
                value: {
                    int: 146,
                },
            },
            {
                time: '2021-08-12 00:00:00',
                value: {
                    int: 130,
                },
            },
            {
                time: '2021-08-13 00:00:00',
                value: {
                    int: 142,
                },
            },
            {
                time: '2021-08-14 00:00:00',
                value: {
                    int: 155,
                },
            },
            {
                time: '2021-08-15 00:00:00',
                value: {
                    int: 111,
                },
            },
            {
                time: '2021-08-16 00:00:00',
                value: {
                    int: 101,
                },
            },
            {
                time: '2021-08-17 00:00:00',
                value: {
                    int: 106,
                },
            },
            {
                time: '2021-08-18 00:00:00',
                value: {
                    int: 127,
                },
            },
            {
                time: '2021-08-19 00:00:00',
                value: {
                    int: 108,
                },
            },
            {
                time: '2021-08-20 00:00:00',
                value: {
                    int: 112,
                },
            },
            {
                time: '2021-08-21 00:00:00',
                value: {
                    int: 171,
                },
            },
            {
                time: '2021-08-22 00:00:00',
                value: {
                    int: 106,
                },
            },
            {
                time: '2021-08-23 00:00:00',
                value: {
                    int: 114,
                },
            },
            {
                time: '2021-08-25 00:00:00',
                value: {
                    int: 142,
                },
            },
            {
                time: '2021-08-26 00:00:00',
                value: {
                    int: 128,
                },
            },
            {
                time: '2021-08-27 00:00:00',
                value: {
                    int: 124,
                },
            },
            {
                time: '2021-08-28 00:00:00',
                value: {
                    int: 99,
                },
            },
        ],
        previous: [
            {
                time: '2021-06-29 0:00:00',
                value: {
                    int: 33,
                },
            },
            {
                time: '2021-06-30 00:00:00',
                value: {
                    int: 41,
                },
            },
            {
                time: '2021-07-01 00:00:00',
                value: {
                    int: 66,
                },
            },
            {
                time: '2021-07-02 00:00:00',
                value: {
                    int: 78,
                },
            },
            {
                time: '2021-07-03 0:00:00',
                value: {
                    int: 44,
                },
            },
            {
                time: '2021-07-04 0:00:00',
                value: {
                    int: 112,
                },
            },
            {
                time: '2021-07-05 00:00:00',
                value: {
                    int: 106,
                },
            },
            {
                time: '2021-07-06 00:00:00',
                value: {
                    int: 90,
                },
            },
            {
                time: '2021-07-07 00:00:00',
                value: {
                    int: 82,
                },
            },
            {
                time: '2021-07-08 00:00:00',
                value: {
                    int: 95,
                },
            },
            {
                time: '2021-07-09 00:00:00',
                value: {
                    int: 70,
                },
            },
            {
                time: '2021-07-10 00:00:00',
                value: {
                    int: 102,
                },
            },
            {
                time: '2021-07-11 00:00:00',
                value: {
                    int: 143,
                },
            },
            {
                time: '2021-07-12 00:00:00',
                value: {
                    int: 109,
                },
            },
            {
                time: '2021-07-13 00:00:00',
                value: {
                    int: 133,
                },
            },
            {
                time: '2021-07-14 00:00:00',
                value: {
                    int: 133,
                },
            },
            {
                time: '2021-07-15 00:00:00',
                value: {
                    int: 120,
                },
            },
            {
                time: '2021-07-16 00:00:00',
                value: {
                    int: 101,
                },
            },
            {
                time: '2021-07-17 00:00:00',
                value: {
                    int: 134,
                },
            },
            {
                time: '2021-07-18 00:00:00',
                value: {
                    int: 123,
                },
            },
            {
                time: '2021-07-19 00:00:00',
                value: {
                    int: 141,
                },
            },
            {
                time: '2021-07-21 00:00:00',
                value: {
                    int: 111,
                },
            },
            {
                time: '2021-07-22 00:00:00',
                value: {
                    int: 100,
                },
            },
            {
                time: '2021-07-23 00:00:00',
                value: {
                    int: 140,
                },
            },
            {
                time: '2021-07-24 00:00:00',
                value: {
                    int: 136,
                },
            },
            {
                time: '2021-07-25 00:00:00',
                value: {
                    int: 117,
                },
            },
            {
                time: '2021-07-26 00:00:00',
                value: {
                    int: 110,
                },
            },
            {
                time: '2021-07-27 00:00:00',
                value: {
                    int: 108,
                },
            },
            {
                time: '2021-07-28 0:00:00',
                value: {
                    int: 42,
                },
            },
        ],
    },
    total: {
        int: 1214,
    },
};
const mockTopSearchesData = {
    summary: {
        main: [
            {
                name: 'total',
                value: {
                    int: 290,
                },
            },
        ],
        previous: []
    },
    terms: [
        buildTerm('shirt', 100, 60),
        buildTerm('skirt', 100, 60),
        buildTerm('jeans', 100, 60),
        buildTerm('pants', 80, 50),
        buildTerm('hat', 60, 25),
        buildTerm('shorts', 30, 14),
        buildTerm('socks', 20, 13),
    ],
    trend: [
        {
            "series": [
                {
                    "time": "2021-07-28 03:00:00",
                    "value": {
                        "int": 2
                    }
                },
                {
                    "time": "2021-07-28 16:00:00",
                    "value": {
                        "int": 1
                    }
                },
                {
                    "time": "2021-07-28 20:00:00",
                    "value": {
                        "int": 12
                    }
                },
                {
                    "time": "2021-07-28 21:00:00",
                    "value": {
                        "int": 3
                    }
                },
                {
                    "time": "2021-07-28 23:00:00",
                    "value": {
                        "int": 4
                    }
                }
            ],
            "term": "shirt"
        },
        {
            "series": [
                {
                    "time": "2021-07-28 20:00:00",
                    "value": {
                        "int": 10
                    }
                },
                {
                    "time": "2021-07-28 21:00:00",
                    "value": {
                        "int": 4
                    }
                }
            ],
            "term": "hat"
        },
        {
            "series": [
                {
                    "time": "2021-07-28 16:00:00",
                    "value": {
                        "int": 10
                    }
                },
                {
                    "time": "2021-07-28 20:00:00",
                    "value": {
                        "int": 2
                    }
                },
                {
                    "time": "2021-07-28 21:00:00",
                    "value": {
                        "int": 2
                    }
                },
                {
                    "time": "2021-07-28 22:00:00",
                    "value": {
                        "int": 16
                    }
                }
            ],
            "term": "pants"
        },
        {
            "series": [
                {
                    "time": "2021-07-28 16:00:00",
                    "value": {
                        "int": 1
                    }
                },
                {
                    "time": "2021-07-28 20:00:00",
                    "value": {
                        "int": 2
                    }
                },
                {
                    "time": "2021-07-28 21:00:00",
                    "value": {
                        "int": 6
                    }
                }
            ],
            "term": "shorts"
        },
        {
            "series": [
                {
                    "time": "2021-07-28 02:00:00",
                    "value": {
                        "int": 4
                    }
                },
                {
                    "time": "2021-07-28 03:00:00",
                    "value": {
                        "int": 2
                    }
                },
                {
                    "time": "2021-07-28 16:00:00",
                    "value": {
                        "int": 12
                    }
                },
                {
                    "time": "2021-07-28 17:00:00",
                    "value": {
                        "int": 2
                    }
                },
                {
                    "time": "2021-07-28 18:00:00",
                    "value": {
                        "int": 2
                    }
                },
                {
                    "time": "2021-07-28 20:00:00",
                    "value": {
                        "int": 78
                    }
                },
                {
                    "time": "2021-07-28 21:00:00",
                    "value": {
                        "int": 99
                    }
                },
                {
                    "time": "2021-07-28 22:00:00",
                    "value": {
                        "int": 101
                    }
                },
                {
                    "time": "2021-07-28 23:00:00",
                    "value": {
                        "int": 375
                    }
                }
            ],
            "term": "socks"
        },
    ]
};
const mockSessionSearchesData = {
    total: {
        int: 10,
        float: 0,
    },
    delta_pct: -14.56,
    series: {
        main: [
            {
                name: 'With sessions',
                value: {
                    int: 4,
                    float: 0,
                },
            },
            {
                name: 'Without sessions',
                value: {
                    int: 6,
                    float: 0,
                },
            },
        ],
        previous: [
            {
                name: 'With sessions',
                value: {
                    int: 3,
                    float: 0,
                },
            },
            {
                name: 'Without sessoions',
                value: {
                    int: 2,
                    float: 0,
                },
            },
        ],
    },
};
const mockNullSearchesData = {
    summary: {
        main: [{ name: 'null', value: { int: 75 } }, { name: 'non-null', value: { int: 60 } }],
        previous: null,
    },
    terms: [
        buildTerm('shirt', 100, 60),
        buildTerm('pants', 80, 50),
        buildTerm('hat', 60, 25),
    ],
    trend: mockTopSearchesData.trend
};

const resultZoneData = {
    zoneType: 'results-list',
    pagination: { page: 0, pages: 2, start: 0, end: 19 },
    hits: 24,
    docs: [
        { type: 'invalid', id: '0', fields: { title: 'test doc 0, foo', url: 'http://example.com/d/1', modified: '2019-01-02T12:12:12Z', created: '2020-01-02T12:12:12Z', languageCode: 'en_US' } },
        { type: 'page', id: '1', fields: { dataType: 'page', title: 'test doc 1, foo', url: 'http://example.com/d/2', modified: '2019-01-02T12:12:12Z', created: '2021-01-02T12:12:12Z', languageCode: 'en_US', 'object.metadata.metaKey': 'test object metadata', 'page.metatags.pageData': 'test page metatags' } },
        { type: 'generic', id: '2', fields: { title: 'test doc 1, foo', url: 'http://example.com/d/3', modified: '2019-01-02T12:12:12Z', created: '2018-01-02T12:12:12Z', languageCode: 'en_US' } },
        { type: 'page', id: '3', fields: { title: 'test doc 3, baz', url: 'http://example.com/d/4', modified: '2018-01-02T12:12:12Z', created: '2019-01-02T12:12:12Z', languageCode: 'en_GB' } },
        { type: 'generic', id: '4', fields: { title: 'test doc 4, baz', url: 'http://example.com/d/5', modified: '2018-01-02T12:12:12Z', created: '2019-01-02T12:12:12Z', languageCode: 'en_GB' } },
        { type: 'generic', id: '5', fields: { title: 'test doc 5, baz', url: 'http://example.com/d/6', modified: '2019-01-02T12:12:12Z', created: '2019-01-02T12:12:12Z', languageCode: 'en_GB' } },
        { type: 'generic', id: '6', fields: { title: 'test doc 6, baz', url: 'http://example.com/d/7', modified: '2017-01-02T12:12:12Z', created: '2018-01-02T12:12:12Z', languageCode: 'en_GB' } },
        { type: 'generic', id: '7', fields: { title: 'test doc 7, baz', url: 'http://example.com/d/8', modified: '2019-01-02T12:12:12Z', created: '2019-01-02T12:12:12Z', languageCode: 'en_GB' } },
        { type: 'generic', id: '8', fields: { title: 'test doc 8, baz', url: 'http://example.com/d/9', modified: '2017-01-02T12:12:12Z', created: '2019-01-02T12:12:12Z', languageCode: 'en_GB' } },
        { type: 'page', id: '9', fields: { title: 'test doc 9, baz', url: 'http://example.com/d/10', modified: '2019-01-02T12:12:12Z', created: '2019-01-02T12:12:12Z', languageCode: 'en_GB' } },
        { type: 'generic', id: '10', fields: { title: 'test doc 10, baz', url: 'http://example.com/d/11', modified: '2019-01-02T12:12:12Z', created: '2019-01-02T12:12:12Z', languageCode: 'en_GB' } },
        { type: 'generic', id: '11', fields: { title: 'test doc 11, bar', url: 'http://example.com/d/12', modified: '2016-01-02T12:12:12Z', created: '2018-01-02T12:12:12Z', languageCode: 'en_US' } },
        { type: 'page', id: '12', fields: { title: 'test doc 12, baz', url: 'http://example.com/d/13', modified: '2020-01-02T12:12:12Z', created: '2019-01-02T12:12:12Z', languageCode: 'en_GB' } },
        { type: 'generic', id: '13', fields: { title: 'test doc 13, baz', url: 'http://example.com/d/14', modified: '2016-01-02T12:12:12Z', created: '2017-01-02T12:12:12Z', languageCode: 'en_GB' } },
        { type: 'generic', id: '14', fields: { title: 'test doc 14, baz', url: 'http://example.com/d/15', modified: '2019-01-02T12:12:12Z', created: '2019-01-02T12:12:12Z', languageCode: 'en_GB' } },
        { type: 'generic', id: '15', fields: { title: 'test doc 15, baz', url: 'http://example.com/d/16', modified: '2019-01-02T12:12:12Z', created: '2016-01-02T12:12:12Z', languageCode: 'en_GB' } },
        { type: 'page', id: '16', fields: { title: 'test doc 16, baz', url: 'http://example.com/d/17', modified: '2019-01-02T12:12:12Z', created: '2019-01-02T12:12:12Z', languageCode: 'en_GB' } },
        { type: 'page', id: '17', fields: { title: 'test doc 17, baz', url: 'http://example.com/d/18', modified: '2019-01-02T12:12:12Z', created: '2015-01-02T12:12:12Z', languageCode: 'en_GB' } },
        { type: 'generic', id: '18', fields: { title: 'test doc 18, baz', url: 'http://example.com/d/19', modified: '2019-01-02T12:12:12Z', created: '2019-01-02T12:12:12Z', languageCode: 'en_GB' } },
        { type: 'generic', id: '20', fields: { title: 'test doc 20, baz', url: 'http://example.com/d/21', modified: '2019-01-02T12:12:12Z', created: '2014-01-02T12:12:12Z', languageCode: 'en_GB' } },
        { type: 'generic', id: '21', fields: { title: 'test doc 21, baz', url: 'http://example.com/d/22', modified: '2019-01-02T12:12:12Z', created: '2019-01-02T12:12:12Z', languageCode: 'en_GB' } },
        { type: 'generic', id: '22', fields: { title: 'test doc 22, baz', url: 'http://example.com/d/23', modified: '2020-01-02T12:12:12Z', created: '2013-01-02T12:12:12Z', languageCode: 'en_GB' } },
        { type: 'generic', id: '23', fields: { title: 'test doc 23, baz', url: 'http://example.com/d/24', modified: '2021-01-02T12:12:12Z', created: '2019-01-02T12:12:12Z', languageCode: 'en_GB' } },
    ]
};

const mockSearchTrendsZoneData = {
    zoneType: 'analytics',
    id: '123',
    config: {
        name: 'Total Searches',
        metricType: 'time-series',
        metricId: 'search/trend',
    },
};
const mockTopSearchesZoneData = {
    zoneType: 'analytics',
    id: '234',
    config: {
        name: 'Top searches, past day',
        metricType: 'bar',
        metricId: 'search/top',
        time: '1H',
    },
    results: mockTopSearchesData
};
const mockNullSearchesZoneData = {
    zoneType: 'analytics',
    id: '345',
    config: { name: 'Searches with 0 results, past day', metricId: 'search/null', metricType: 'bar', time: '1D' },
    results: mockNullSearchesData
};
const mockOneSearchesZoneData = Object.assign(Object.assign({}, mockTopSearchesZoneData), { id: '456', config: Object.assign(Object.assign({}, mockTopSearchesZoneData.config), { name: 'Search terms with 1 result, past day', metricId: 'search/one' }), results: Object.assign(Object.assign({}, mockTopSearchesData), { summary: {
            main: [
                { name: 'one', value: { int: 10 } },
                { name: 'zero', value: { int: 15 } },
                { name: 'two-plus', value: { int: 50 } },
            ],
            previous: null,
        } }) });
const mockSessionSearchesZoneData = {
    zoneType: 'analytics',
    id: '567',
    config: {
        name: 'Sessions with searches, past day',
        metricType: 'donut',
        metricId: 'search/sessions',
        time: '1H',
    },
    results: mockSessionSearchesData
};
const mockUniqueUsersZoneData = Object.assign(Object.assign({}, mockSessionSearchesZoneData), { id: '678', config: Object.assign(Object.assign({}, mockSessionSearchesZoneData.config), { name: 'Unique users, past day', metricId: 'users/unique' }), results: Object.assign(Object.assign({}, mockSessionSearchesData), { series: {
            main: lodash.map(mockSessionSearchesData.series.main, item => (Object.assign(Object.assign({}, item), { name: item.name === 'With sessions' ? 'Unique users' : 'Returning users' }))),
            previous: lodash.map(mockSessionSearchesData.series.previous, item => (Object.assign(Object.assign({}, item), { name: item.name === 'With sessions' ? 'Unique users' : 'Returning users' })))
        } }) });
const mockResolvedSearchesZoneData = Object.assign(Object.assign({}, mockSessionSearchesZoneData), { id: '789', config: Object.assign(Object.assign({}, mockSessionSearchesZoneData.config), { name: 'Resolved Searches with 0 Results, past day', metricId: 'search/resolved' }), results: Object.assign(Object.assign({}, mockSessionSearchesData), { series: {
            main: lodash.map(mockSessionSearchesData.series.main, item => (Object.assign(Object.assign({}, item), { name: item.name === 'With sessions' ? 'Resolved by smart search' : 'Searches with 0 results' }))),
            previous: lodash.map(mockSessionSearchesData.series.previous, item => (Object.assign(Object.assign({}, item), { name: item.name === 'With sessions' ? 'Resolved by smart search' : 'Searches with 0 results' })))
        } }) });
const mockRefinedSearchesZoneData = Object.assign(Object.assign({}, mockSessionSearchesZoneData), { id: '890', config: Object.assign(Object.assign({}, mockSessionSearchesZoneData.config), { name: 'Refined searches, past day', metricId: 'search/refined' }), results: Object.assign(Object.assign({}, mockSessionSearchesData), { series: {
            main: lodash.map(mockSessionSearchesData.series.main, item => (Object.assign(Object.assign({}, item), { name: item.name === 'With sessions' ? 'Refined' : 'Without refinements' }))),
            previous: lodash.map(mockSessionSearchesData.series.previous, item => (Object.assign(Object.assign({}, item), { name: item.name === 'With sessions' ? 'Refined' : 'Without refinements' })))
        } }) });
const getMockSearchTrendsZoneData = (time = '1D') => {
    let searchData;
    switch (time) {
        case '1H':
            searchData = mockSearchTrendsDataHourly;
            break;
        case '1D':
            searchData = mockSearchTrendsDataDaily;
            break;
        case '1W':
            searchData = mockSearchTrendsDataWeekly;
            break;
        case '1M':
            searchData = mockSearchTrendsDataMonthly;
            break;
        default:
            searchData = mockSearchTrendsDataHourly;
    }
    return Object.assign(Object.assign({}, mockSearchTrendsZoneData), { config: Object.assign(Object.assign({}, mockSearchTrendsZoneData.config), { time }), results: searchData });
};
const getTypeaheadZoneData = (query, data) => {
    const docs = data.docs
        .filter((doc) => doc.fields.title.includes(query))
        .map((doc) => (Object.assign(Object.assign({}, doc), { fields: { title: doc.fields.title.replace(query, `<em>${query}</em>`) } })))
        .slice(0, 10);
    return {
        zoneType: 'typeahead',
        hits: docs.length,
        docs,
    };
};
const sortList$2 = (docs, sortParam) => {
    if (lodash.includes(['oldest-published', 'newest-published'], sortParam)) {
        docs = lodash.orderBy(docs, ['fields.created'], [sortParam === 'oldest-published' ? 'asc' : 'desc']);
    }
    return docs;
};
const searchResultsConfig$2 = {
    facets: ['languageCode', 'type'],
    rows: 20,
};
const getMockData$2 = ({ type, params, time }) => {
    const query = params.query === '*:*' ? '' : params.query;
    let zones;
    switch (type) {
        case 'searchResults':
            const { facets, rows } = searchResultsConfig$2;
            const page = params.page || 0;
            const rowDocs = query ? resultZoneData.docs.filter((doc) => doc.fields.title.includes(query)) : resultZoneData.docs;
            const docs = lodash.cloneDeep(sortList$2(rowDocs, params.sort));
            let pagination;
            let facetFieldMap;
            if (!lodash.isEmpty(params.filters)) {
                lodash.each(params.filters, ({ field, value }) => {
                    lodash.remove(docs, (doc) => doc.fields[field] !== value);
                });
            }
            facetFieldMap = lodash.transform(facets, (result, field) => {
                const fieldDocs = lodash.groupBy(docs, `fields.${field}`);
                result[field] = {
                    counts: lodash.map(fieldDocs, (facetValueDocs, facetName) => ({
                        name: facetName,
                        count: facetValueDocs.length,
                    })),
                };
            }, {});
            pagination = {
                page,
                start: page * rows,
                end: Math.min((page + 1) * rows - 1, docs.length),
            };
            zones = [
                {
                    zoneType: 'facets',
                    facets: { field: facetFieldMap },
                },
                Object.assign(Object.assign({}, resultZoneData), { hits: docs.length, pagination, docs: docs.slice(pagination.start, pagination.end + 1) })
            ];
            break;
        case 'typeahead':
            zones = [getTypeaheadZoneData(query, resultZoneData)];
            break;
        case 'detail':
            zones = [{
                    zoneType: 'detail',
                    doc: lodash.find(resultZoneData.docs, { id: params.id }),
                }];
            break;
        case 'analytics':
            zones = [
                getMockSearchTrendsZoneData(time),
                mockTopSearchesZoneData,
                mockOneSearchesZoneData,
                mockNullSearchesZoneData,
                mockSessionSearchesZoneData,
                mockUniqueUsersZoneData,
                mockRefinedSearchesZoneData,
                mockResolvedSearchesZoneData,
            ];
    }
    return { zones };
};
const zoneDataMap = {
    'search/top': mockTopSearchesZoneData
};
const getMockZone = (config) => {
    return zoneDataMap[config.metricId];
};

const renderStoreMockHelpers = /*#__PURE__*/Object.freeze({
    __proto__: null,
    mockSearchTrendsZoneData: mockSearchTrendsZoneData,
    mockTopSearchesZoneData: mockTopSearchesZoneData,
    mockNullSearchesZoneData: mockNullSearchesZoneData,
    mockOneSearchesZoneData: mockOneSearchesZoneData,
    mockSessionSearchesZoneData: mockSessionSearchesZoneData,
    mockUniqueUsersZoneData: mockUniqueUsersZoneData,
    mockResolvedSearchesZoneData: mockResolvedSearchesZoneData,
    mockRefinedSearchesZoneData: mockRefinedSearchesZoneData,
    getMockSearchTrendsZoneData: getMockSearchTrendsZoneData,
    getMockData: getMockData$2,
    getMockZone: getMockZone
});

class RenderStoreMockPlugin extends DataStoreMockPlugin {
    constructor() {
        super(...arguments);
        this.mockApis = [...DataStoreMockPlugin.crudMockApis, 'doRequest', 'getMetricDetail'];
    }
    /**
     * @override
     */
    async getMockData(type = this.store.templateType, params = this.store.currentParams) {
        const mockDataArgs = { type, params };
        const meta = this.store.getMeta();
        if (type === 'analytics' && lodash.get(meta, 'metricTime')) {
            mockDataArgs.time = meta.metricTime;
        }
        return Promise.resolve(getMockData$2(mockDataArgs));
    }
    async getMetricDetail(config) {
        return Promise.resolve(getMockZone(config));
    }
    async doRequest() {
        return this.getMockData();
    }
}

class ZoneStoreMockPlugin extends DataStoreMockPlugin {
    constructor() {
        super(...arguments);
        this.mockApis = [...DataStoreMockPlugin.crudMockApis, 'doUpdate'];
    }
    // /**
    //  * @override
    //  */
    // async getMockData(type = this.store.templateType, params = this.store.currentParams) {
    //   return Promise.resolve(mockHelpers.getMockData({ type, params }));
    // }
    async doUpdate() {
        return Promise.resolve({});
    }
}

class ZoneStore extends MoBaseStore {
    constructor(data, config) {
        var _a;
        var _b;
        super([], config);
        this.mockPluginApplied = false;
        this.getApiService = () => getStoreApiService();
        this.updateConfig = async (config, zoneId) => {
            await this.doUpdate(config, zoneId);
            this.notifyAction(Object.assign({}, config));
        };
        this.isQueryStoreParams = (params) => params === null || params === void 0 ? void 0 : params.sessionId;
        this.onAction = config === null || config === void 0 ? void 0 : config.onAction;
        this.setData(data, config === null || config === void 0 ? void 0 : config.params);
        (_a = (_b = this.config).mock) !== null && _a !== void 0 ? _a : (_b.mock = this.getApiService().meta.config.mock);
        const { mock, mockPlugin } = this.config;
        if (mock && mockPlugin && !this.mockPluginApplied) {
            const appliedMockPlugin = new mockPlugin(this);
            appliedMockPlugin.applyMocks();
            this.mockPluginApplied = true;
        }
    }
    setQuery(query) {
        this.notifyAction({ query });
    }
    setSort(sort) {
        this.notifyAction({ sort });
    }
    setPage(page = 0) {
        this.notifyAction({ page });
    }
    setFacets(facets) {
        this.notifyAction({ filters: facets });
    }
    setData(data, renderParams, isRawResponse = true) {
        this.data = isRawResponse ? this.transform(data, renderParams) : data;
        this.notifyUpdates();
        return this;
    }
    async doUpdate(config, zoneId) {
        const zone = await (this.getApiService().invoke('zones', 'getZone', { pathParams: { id: zoneId } }));
        const version = lodash.get(zone, 'version');
        return this.getApiService().invoke('zones', 'updateZone', { data: Object.assign({}, config), pathParams: { id: zoneId }, headers: { 'If-Match': `"${version}"` } });
    }
    transform(data, requestParams) {
        var _a;
        const isQueryStore = this.isQueryStoreParams(requestParams);
        const filterParams = {
            field: 'field',
            value: isQueryStore ? 'values' : 'value'
        };
        if (data.zoneType === 'facets') {
            const facets = lodash((_a = data.facets) === null || _a === void 0 ? void 0 : _a.field).pickBy('counts').map(({ counts }, title) => ({
                title,
                facets: counts.map((item) => ({ title, value: item.name, count: item.count })),
            })).value();
            data.facets = facets;
            if (lodash.has(requestParams, 'facets')) {
                const requestFacets = requestParams.facets;
                data.facets = facets.sort((a, b) => {
                    return lodash.indexOf(requestFacets, a.title) > lodash.indexOf(requestFacets, b.title) ? 1 : -1;
                });
            }
            data.selectedFacets = lodash.map(requestParams === null || requestParams === void 0 ? void 0 : requestParams.filters, (filter) => ({ title: filter[filterParams.field], value: isQueryStore ? lodash.first(filter[filterParams.value]) : filter[filterParams.value], }));
        }
        else if (data.zoneType === 'results-list') {
            // Default page size - search-results: 20, typeahead: 10
            // https://github.com/lucidworks/fusion-templating/blob/main/templating/src/main/resources/application.yml#L106-L107
            const { start, end } = data.pagination || {};
            data.pagination = { numFound: data.hits, start, rows: end - start + 1 };
        }
        else if (lodash.get(data, 'config.zoneType') === 'analytics') {
            const subpaths = ['summary.main', 'summary.previous', 'series.main', 'series.previous', 'terms', 'trend'];
            lodash.forEach(subpaths, path => {
                path = `results.${path}`;
                if (lodash.isNull(lodash.get(data, path))) {
                    lodash.set(data, path, []);
                }
            });
        }
        return data;
    }
    notifyAction(meta) {
        if (this.onAction) {
            this.onAction(Object.assign(Object.assign({}, meta), { store: this }));
        }
    }
}

class RenderStore extends MoBaseStore {
    constructor(config) {
        var _a;
        var _b;
        super([], Object.assign({ 
            // It should notify with the same zone store references for components to subscribe to.
            cloneNotificationPayload: false, requestDebounceWait: 100, mockPlugin: RenderStoreMockPlugin }, config));
        this.data = [];
        this.initLoad = false;
        this.loaded = false;
        this.mockPluginApplied = false;
        this.requestParamsByType = {
            searchResults: ['query', 'sort', 'filters', 'page'],
            typeahead: ['query', 'sort', 'filters', 'page'],
            detail: ['id'],
            analytics: ['atTime'],
            relatedCasesTypeahead: ['query', 'sort', 'filters', 'page'],
            relatedCases: ['query', 'sort', 'filters', 'page'],
            relatedArticles: ['query', 'sort', 'filters', 'page'],
            relatedArticlesTypeahead: ['query', 'sort', 'filters', 'page'],
            relatedExperts: ['query', 'sort', 'filters', 'page'],
            relatedExpertsTypeahead: ['query', 'sort', 'filters', 'page'],
        };
        this.getApiService = () => getStoreApiService();
        this.loadData = (zones) => {
            this.setZoneStores(zones, undefined);
            this.initLoad = true;
            this.loaded = true;
        };
        this.templateType = config.type;
        this.doRequest = dist(this.doRequest, this.config.requestDebounceWait);
        this.currentParams = lodash.includes(['searchResults', 'typeahead', 'relatedCasesTypeahead', 'relatedCases', 'relatedArticles', 'relatedArticlesTypeahead', 'relatedExperts', 'relatedExpertsTypeahead'], this.templateType) ? { query: '*:*' } : {};
        (_a = (_b = this.config).mock) !== null && _a !== void 0 ? _a : (_b.mock = this.getApiService().meta.config.mock);
        const { mock, mockPlugin } = this.config;
        if (mock && mockPlugin && !this.mockPluginApplied) {
            const appliedMockPlugin = new mockPlugin(this);
            appliedMockPlugin.applyMocks();
            this.mockPluginApplied = true;
        }
    }
    /**
     * @override
     */
    getMeta() {
        return this.cloneProps(['config', 'data', 'currentParams', 'metricTime']);
    }
    /**
     * @override
     */
    subscribe(callback, component) {
        const unSubscribe = super.subscribe(callback, component);
        if (!this.initLoad) {
            this.initLoad = true;
            this.request().then(() => this.loaded = true);
        }
        return unSubscribe;
    }
    async setTemplateType(type) {
        this.templateType = type;
        this.request(this.currentParams);
    }
    setQuery(query) {
        return this.request({ query });
    }
    setFacets(filters) {
        return this.request({ filters });
    }
    setPage(page) {
        return this.request({ page });
    }
    setSort(sort) {
        return this.request({ sort });
    }
    async getDocDetail(docId) {
        const response = await this.request({ id: docId });
        return lodash.get(response, 'zones[0].doc');
    }
    /**
     * https://lucidworks.atlassian.net/wiki/spaces/SIN/pages/1619722481/Sitesearch+Zone+and+Template+Types
     *
     * Params for 'search-results' and 'typeahead':
     *
     * {
     *   "query": "query terms here",
     *   "page": 0,
     *
     *   "filters": [
     *     {"field": "extension", "value": "pdf"},
     *     {"field": "languageCode", "value": "en_US"},
     *     {"field": "datasourceTag", "value": "tag1"}
     *   ]
     * }
     *
     * Param for 'detail'
     *
     * {
     *   "id": "doc-id",
     * }
     *
     */
    async request(params, updateIndex) {
        let response;
        this.setActionParams(params);
        response = await this.doRequest();
        this.setZoneStores(lodash.filter(response.zones, zone => lodash.isEmpty(zone.errorMessage)) || [], updateIndex);
        this.notifyUpdates();
        return response;
    }
    /**
     * @override
     */
    notifyUpdatesOnSubscribe(callback) {
        if (this.loaded) {
            super.notifyUpdatesOnSubscribe(callback);
        }
    }
    async getMetricDetail(config, atTime) {
        return (this.getApiService().invoke('render', 'getMetricDetail', { data: { zones: [config], atTime } }) || {});
    }
    async doRequest() {
        return (this.getApiService().invoke('render', this.templateType, { data: this.currentParams }) || {});
    }
    setActionParams(params) {
        const supportedParams = this.requestParamsByType[this.templateType];
        const newParams = lodash.pick(params, supportedParams);
        // There will be at most one facet per store action.
        const targetFacet = lodash.first(newParams.filters);
        if (targetFacet) {
            // This also un-toggles the existing facet value from `this.currentParams.filters`.
            const isDeselect = !lodash(this.currentParams.filters).remove(targetFacet).isEmpty();
            // Remove all existing facet values for the same facet as render API only supports ANDs within facets.
            lodash.remove(this.currentParams.filters, { field: targetFacet.field });
            if (!isDeselect) {
                this.currentParams.filters = [...(this.currentParams.filters || []), targetFacet];
            }
        }
        else {
            // Overwrite current params on top level, this also clears all existing filters with params `{ filters: [] }`.
            this.currentParams = Object.assign(Object.assign({}, this.currentParams), newParams);
        }
        // Reset to the first page when changing query or filters.
        if (lodash.intersection(['query', 'filters'], lodash.keys(params)).length) {
            this.currentParams.page = 0;
        }
        // Clear the previous sort param.
        if (!newParams.sort) {
            delete this.currentParams.sort;
        }
    }
    setZoneStores(zones, updateIndex) {
        lodash.each(zones, (zone, index) => {
            if (!this.data[index]) {
                this.data[index] = new ZoneStore(zone, {
                    index,
                    onAction: lodash.bind(this.onZoneAction, this),
                    mock: this.config.mock,
                    mockPlugin: this.config.mock && ZoneStoreMockPlugin
                });
            }
            else if (lodash.isUndefined(updateIndex) || updateIndex === index) {
                this.data[index].setData(zone, lodash.cloneDeep(this.currentParams));
            }
        });
    }
    /**
     * Only refresh the zone if the action is specific to the zone itself, or do global refresh.
     */
    onZoneAction(meta) {
        var _a;
        const noneGlobalActions = ['sort', 'page'];
        const zoneToUpdate = !lodash(meta).pick(noneGlobalActions).isEmpty() && meta.store;
        const indexToUpdate = (_a = zoneToUpdate.meta) === null || _a === void 0 ? void 0 : _a.config.index;
        this.request(meta, indexToUpdate);
    }
}

const queryZoneData = {
    hits: 24,
    pagination: { page: 0, pages: 2, start: 0, end: 19 },
    docs: [
        { type: 'page', id: 'http://example.com/d/0', score: 11.0, datasourceLabels: ['bbc'], fields: { 'document.title': 'test doc 0, foo', 'document.url': 'http://example.com/d/0', 'document.description': 'Test Doc 0 is foo', 'document.languageCode': 'en' } },
        { type: 'file', id: 'http://example.com/d/1', score: 11.0, datasourceLabels: ['bbc'], fields: { 'document.title': 'test doc 1, bar', 'document.url': 'http://example.com/d/1', 'document.description': 'Test Doc 1 is bar', 'document.languageCode': 'en', 'file.extension': 'pdf' } },
        { type: 'case', id: 'http://example.com/d/2', score: 11.0, datasourceLabels: ['salesforce'], fields: { 'document.title': 'test doc 2, baz', 'document.url': 'http://example.com/d/2', 'document.languageCode': 'en', 'case.agentName': 'Agent Casey McCaseyface', 'case.caseNumber': 'case-12305', 'object.metadata.metaKey': 'test object metadata', 'page.metatags.pageData': 'test page metatags' } },
        { type: 'page', id: 'http://example.com/d/3', score: 11.0, datasourceLabels: ['bbc'], fields: { 'document.title': 'test doc 3, foo', 'document.url': 'http://example.com/d/3', 'document.description': 'Test Doc 3 is foo', 'document.languageCode': 'en' } },
        { type: 'page', id: 'http://example.com/d/4', score: 11.0, datasourceLabels: ['bbc'], fields: { 'document.title': 'test doc 4, foo', 'document.url': 'http://example.com/d/4', 'document.description': 'Test Doc 4 is foo', 'document.languageCode': 'en' } },
        { type: 'file', id: 'http://example.com/d/5', score: 11.0, datasourceLabels: ['bbc'], fields: { 'document.title': 'test doc 5, bar', 'document.url': 'http://example.com/d/5', 'document.description': 'Test Doc 5 is bar', 'document.languageCode': 'en', 'file.extension': 'pdf' } },
        { type: 'case', id: 'http://example.com/d/6', score: 11.0, datasourceLabels: ['salesforce'], fields: { 'document.title': 'test doc 6, baz', 'document.url': 'http://example.com/d/6', 'document.languageCode': 'en', 'case.agentName': 'Agent Casey McCaseyface', 'case.caseNumber': 'case-12306' } },
        { type: 'case', id: 'http://example.com/d/7', score: 11.0, datasourceLabels: ['salesforce'], fields: { 'document.title': 'test doc 7, baz', 'document.url': 'http://example.com/d/7', 'document.languageCode': 'en', 'case.agentName': 'Agent Casey McCaseyface', 'case.caseNumber': 'case-12307' } },
        { type: 'case', id: 'http://example.com/d/8', score: 11.0, datasourceLabels: ['salesforce'], fields: { 'document.title': 'test doc 8, baz', 'document.url': 'http://example.com/d/8', 'document.languageCode': 'en', 'case.agentName': 'Agent Casey McCaseyface', 'case.caseNumber': 'case-12308' } },
        { type: 'page', id: 'http://example.com/d/9', score: 11.0, datasourceLabels: ['bbc'], fields: { 'document.title': 'test doc 9, foo', 'document.url': 'http://example.com/d/9', 'document.description': 'Test Doc 9 is foo', 'document.languageCode': 'en' } },
        { type: 'file', id: 'http://example.com/d/10', score: 11.0, datasourceLabels: ['bbc'], fields: { 'document.title': 'test doc 10, bar', 'document.url': 'http://example.com/d/10', 'document.description': 'Test Doc 10 is bar', 'document.languageCode': 'en', 'file.extension': 'pdf' } },
        { type: 'file', id: 'http://example.com/d/11', score: 11.0, datasourceLabels: ['bbc'], fields: { 'document.title': 'test doc 11, bar', 'document.url': 'http://example.com/d/11', 'document.description': 'Test Doc 11 is bar', 'document.languageCode': 'en', 'file.extension': 'pdf' } },
        { type: 'file', id: 'http://example.com/d/12', score: 11.0, datasourceLabels: ['bbc'], fields: { 'document.title': 'test doc 12, bar', 'document.url': 'http://example.com/d/12', 'document.description': 'Test Doc 12 is bar', 'document.languageCode': 'en', 'file.extension': 'txt' } },
        { type: 'page', id: 'http://example.com/d/13', score: 11.0, datasourceLabels: ['bbc'], fields: { 'document.title': 'test doc 13, foo', 'document.url': 'http://example.com/d/13', 'document.description': 'Test Doc 13 is foo', 'document.languageCode': 'es', 'file.extension': 'html' } },
        { type: 'page', id: 'http://example.com/d/14', score: 11.0, datasourceLabels: ['bbc'], fields: { 'document.title': 'test doc 14, foo', 'document.url': 'http://example.com/d/14', 'document.description': 'Test Doc 14 is foo', 'document.languageCode': 'es', 'file.extension': 'pdf' } },
        { type: 'page', id: 'http://example.com/d/15', score: 11.0, datasourceLabels: ['bbc'], fields: { 'document.title': 'test doc 15, foo', 'document.url': 'http://example.com/d/15', 'document.description': 'Test Doc 15 is foo', 'document.languageCode': 'es', 'file.extension': 'pdf' } },
        { type: 'page', id: 'http://example.com/d/16', score: 11.0, datasourceLabels: ['bbc'], fields: { 'document.title': 'test doc 16, foo', 'document.url': 'http://example.com/d/16', 'document.description': 'Test Doc 16 is foo', 'document.languageCode': 'es', 'file.extension': 'html' } },
        { type: 'case', id: 'http://example.com/d/17', score: 11.0, datasourceLabels: ['salesforce'], fields: { 'document.title': 'test doc 17, baz', 'document.url': 'http://example.com/d/17', 'document.languageCode': 'en', 'case.agentName': 'Agent Casey McCaseyface', 'case.caseNumber': 'case-12317' } },
        { type: 'case', id: 'http://example.com/d/18', score: 11.0, datasourceLabels: ['salesforce'], fields: { 'document.title': 'test doc 18, baz', 'document.url': 'http://example.com/d/18', 'document.languageCode': 'en', 'case.agentName': 'Agent Casey McCaseyface', 'case.caseNumber': 'case-12318' } },
        { type: 'page', id: 'http://example.com/d/19', score: 11.0, datasourceLabels: ['bbc'], fields: { 'document.title': 'test doc 19, foo', 'document.url': 'http://example.com/d/19', 'document.description': 'Test Doc 19 is foo', 'document.languageCode': 'es' } },
        { type: 'page', id: 'http://example.com/d/20', score: 11.0, datasourceLabels: ['bbc'], fields: { 'document.title': 'test doc 20, foo', 'document.url': 'http://example.com/d/20', 'document.description': 'Test Doc 20 is foo', 'document.languageCode': 'fr', 'file.extension': 'pdf' } },
        { type: 'file', id: 'http://example.com/d/21', score: 11.0, datasourceLabels: ['bbc'], fields: { 'document.title': 'test doc 21, bar', 'document.url': 'http://example.com/d/21', 'document.description': 'Test Doc 21 is bar', 'document.languageCode': 'fr', 'file.extension': 'txt' } },
        { type: 'page', id: 'http://example.com/d/22', score: 11.0, datasourceLabels: ['bbc'], fields: { 'document.title': 'test doc 22, foo', 'document.url': 'http://example.com/d/22', 'document.description': 'Test Doc 22 is foo', 'document.languageCode': 'fr', 'file.extension': 'html' } },
        { type: 'file', id: 'http://example.com/d/23', score: 11.0, datasourceLabels: ['bbc'], fields: { 'document.title': 'test doc 23, bar', 'document.url': 'http://example.com/d/23', 'document.description': 'Test Doc 23 is bar', 'document.languageCode': 'fr', 'file.extension': 'txt' } },
    ],
    facets: {
        field: {
            'file.extension': {
                missing: 0,
                counts: [
                    {
                        name: 'pdf',
                        count: 7
                    }
                ]
            }
        }
    }
};
Object.assign({}, queryZoneData);

const getSearchaheadZoneData = (query, data) => {
    const docs = data.docs
        .filter((doc) => lodash.toLower(doc.fields['document.title']).includes(lodash.toLower(query)))
        .map((doc) => (Object.assign(Object.assign({}, doc), { fields: { title: doc.fields['document.title'].replace(new RegExp(`(${query})`, 'gi'), `<em>$1</em>`) } })))
        .slice(0, 10);
    return {
        hits: docs.length,
        docs,
    };
};
const sortList$1 = (docs, sortParam) => {
    if (lodash.includes(['oldest-published', 'newest-published'], sortParam)) {
        docs = lodash.orderBy(docs, ['fields.document.sourceCreateTimestamp'], [sortParam === 'oldest-published' ? 'asc' : 'desc']);
    }
    return docs;
};
const searchResultsConfig$1 = {
    facets: ['file.extension', 'document.languageCode'],
    rows: 20,
};
const getMockApiData$1 = () => queryZoneData;
const getMockData$1 = ({ type, params }) => {
    const query = params.query === '*:*' ? '' : params.query;
    const { facets, rows } = searchResultsConfig$1;
    const page = params.page || 0;
    let rowDocs = getMockApiData$1().docs;
    if (query) {
        rowDocs = rowDocs.filter((doc) => lodash.toLower(doc.fields['document.title']).includes(lodash.toLower(query)));
    }
    const docs = lodash.chain(sortList$1(rowDocs, params.sort)).cloneDeep().map((doc) => (Object.assign(Object.assign({}, doc), { fields: Object.assign(Object.assign({}, doc.fields), { 'document.description': lodash.replace(doc.fields['document.description'], new RegExp(`(${query})`, 'gi'), '<em>$1</em>') }) }))).value();
    let pagination;
    let facetFieldMap;
    switch (type) {
        case 'search':
            if (!lodash.isEmpty(params.filters)) {
                lodash.each(params.filters, ({ field, values }) => {
                    lodash.remove(docs, (doc) => !lodash.includes(values, doc.fields[field]));
                });
            }
            facetFieldMap = lodash.transform(facets, (result, field) => {
                const fieldDocs = lodash.reduce(docs, (res, doc) => {
                    if (lodash.has(doc.fields, field) && doc.fields[field]) {
                        if (lodash.has(res, doc.fields[field])) {
                            res[doc.fields[field]].push(doc);
                        }
                        else {
                            res[doc.fields[field]] = [doc];
                        }
                    }
                    return res;
                }, {});
                const counts = lodash.map(fieldDocs, (facetValueDocs, facetName) => ({
                    name: facetName,
                    count: facetValueDocs.length,
                }));
                if (!lodash.isEmpty(counts)) {
                    result[field] = { counts };
                }
                return result;
            }, {});
            pagination = {
                page,
                start: page * rows,
                end: Math.min((page + 1) * rows - 1, docs.length),
            };
            return Object.assign(Object.assign({}, !lodash.isEmpty(facetFieldMap) && { facets: { field: facetFieldMap } }), { hits: docs.length, docs: docs.slice(pagination.start, pagination.end + 1) });
        case 'searchahead':
            return getSearchaheadZoneData(query, getMockApiData$1());
        case 'detail':
            return lodash.find(getMockApiData$1().docs, { id: params.id });
        case 'relatedContent':
            pagination = {
                page,
                start: page * rows,
                end: Math.min((page + 1) * rows - 1, docs.length),
            };
            return {
                hits: docs.length,
                docs: docs.slice(pagination.start, pagination.end + 1),
            };
    }
    return {};
};

class QueryStoreMockPlugin extends DataStoreMockPlugin {
    constructor() {
        super(...arguments);
        this.mockApis = [...DataStoreMockPlugin.crudMockApis, 'doRequest', 'getRulesByQuery', 'getGlobalRules', 'getDoc', 'getQueriesWithAction'];
    }
    /**
     * @override
     */
    async getMockData(type = this.store.queryType, params = this.store.currentParams) {
        return Promise.resolve(getMockData$1({ type, params }));
    }
    async doRequest() {
        return this.getMockData();
    }
    async getRulesByQuery() {
        return [{ id: 123, config: { docId: 234 } }];
    }
    async getGlobalRules() {
        return [{ id: 123, config: { docId: 345, type: 'globalBlock' } }];
    }
    async getQueriesWithAction() {
        return ['dog', 'cat', 'pony'];
    }
    async getDoc(id) {
        return { id, type: 'page', fields: { "document.title": 'foo' } };
    }
}

class QueryStore extends MoModel {
    constructor(config) {
        var _a;
        var _b, _c;
        super([], config = Object.assign({ 
            // It should notify with the same zone store references for components to subscribe to.
            cloneNotificationPayload: false, storeDataInZones: true, requestDebounceWait: 100, mockPlugin: QueryStoreMockPlugin }, config));
        this.initLoad = false;
        this.loaded = false;
        this.mockPluginApplied = false;
        this.requestParamsByType = {
            search: ['query', 'sort', 'filters', 'page', 'utcOffset', 'fieldList', 'facets', 'sessionId', 'limit', 'highlight'],
            searchahead: ['query', 'sort', 'filters', 'page', 'utcOffset', 'fieldList', 'sessionId', 'limit', 'highlight'],
            detail: ['id', 'utcOffset', 'fieldList', 'sessionId'],
            relatedContent: ['probe', 'utcOffset', 'fieldList', 'sessionId', 'limit', 'source'],
            signals: ['signals', 'userId']
        };
        this.getApiService = () => getStoreApiService();
        this.hasDocs = (zoneStores) => lodash.some(zoneStores, zone => { var _a; return !lodash.isEmpty((_a = zone.meta.data) === null || _a === void 0 ? void 0 : _a.docs); });
        this.config = config;
        this.queryType = config.type;
        this.doRequest = dist(this.doRequest, this.config.requestDebounceWait);
        this.currentParams = lodash.includes(['search', 'searchahead'], this.queryType) ? { query: '*:*', highlight: true } : {};
        // Deprecated by config.queryParams = { facets }
        if (config.facets) {
            this.currentParams.facets = config.facets;
        }
        if (this.queryType === 'search') {
            (_b = this.currentParams).facets || (_b.facets = ['file.extension', 'document.languageCode', 'datasourceLabels', 'type']);
        }
        this.currentParams = Object.assign(Object.assign({}, this.currentParams), this.getDefaultRequestParams());
        (_a = (_c = this.config).mock) !== null && _a !== void 0 ? _a : (_c.mock = this.getApiService().meta.config.mock);
        if (config.queryParams) {
            this.setActionParams(config.queryParams);
        }
        const { mock, mockPlugin } = this.config;
        if (mock && mockPlugin && !this.mockPluginApplied) {
            const appliedMockPlugin = new mockPlugin(this);
            appliedMockPlugin.applyMocks();
            this.mockPluginApplied = true;
        }
    }
    /**
     * @override
     */
    getMeta() {
        return this.cloneProps(['config', 'data', 'currentParams']);
    }
    /**
     * @override
     */
    subscribe(callback, component) {
        const unSubscribe = super.subscribe(callback, component);
        if (!this.config.lazyLoadOnSubscribe && !this.initLoad) {
            this.initLoad = true;
            this.request().then(() => this.loaded = true);
        }
        return unSubscribe;
    }
    /**
     * Update the query params and follow up by request update if the store has loaded initially.
     */
    async setQueryParams(params) {
        this.setActionParams(params);
        if (this.initLoad) {
            await this.request();
        }
    }
    setSearch(params) {
        const { query, page, sort, facets: filters } = params;
        return this.request({ query, page, sort, filters });
    }
    ;
    setQuery(query) {
        return this.request({ query });
    }
    setQueryId(queryId) {
        this.currentParams.queryId = queryId;
    }
    /**
     * Set facets with two supported modes:
     *
     * - `Toggle`: If the facet list only contains a single facet, toggle this facet in current params.
     * - `Overwrite`: If the facet list contains multiple facets or it's empty, sync up the current params with it.
     */
    setFacets(facets) {
        return this.request({ filters: facets });
    }
    setPage(page) {
        return this.request({ page });
    }
    setSort(sortField, sortOrder) {
        return this.request({ sort: [{ sortField, sortOrder }] });
    }
    setProbe(doc) {
        if (doc) {
            if (lodash.get(doc, 'id')) {
                this.currentParams.probe = {
                    documentId: lodash.get(doc, 'id')
                };
            }
            else {
                this.currentParams.probe = {
                    title: lodash.get(doc.fields, 'document.title'),
                    description: lodash.get(doc.fields, 'document.description'),
                    caseId: lodash.get(doc.fields, 'case.caseId'),
                };
            }
        }
    }
    async getDocDetail(docId) {
        return this.request({ id: docId });
    }
    /**
     * Stateless functions which take the low-level Query API request params.
     */
    search(request) {
        const params = lodash.isString(request) ? Object.assign(Object.assign({}, this.getDefaultRequestParams()), { query: request }) : Object.assign(Object.assign({}, this.getDefaultRequestParams()), request);
        return this.doRequest(params, 'search');
    }
    ;
    browse(request) {
        return this.search(lodash.omit(Object.assign({}, request), 'query'));
    }
    clickSignal(request) {
        return this.sendSignal('click', request);
    }
    querySignal(request) {
        return this.sendSignal('query', request);
    }
    sendSignal(signalType, request) {
        const signal = Object.assign(Object.assign({}, lodash.omit(this.getDefaultRequestParams(), 'debug')), { appType: 'SITESEARCH', signalType, timestamp: Date.now(), queryId: this.currentParams.queryId, userId: this.config.userId });
        return (this.getApiService().invoke('signals', 'signals', { data: [lodash.merge(signal, request)] }) || {});
    }
    searchahead(request) {
        const params = lodash.isString(request) ? Object.assign(Object.assign({}, this.getDefaultRequestParams()), { query: request }) : Object.assign(Object.assign({}, this.getDefaultRequestParams()), request);
        return this.doRequest(params, 'searchahead');
    }
    detail(request) {
        const params = lodash.isString(request) ? Object.assign(Object.assign({}, this.getDefaultRequestParams()), { id: request }) : Object.assign(Object.assign({}, this.getDefaultRequestParams()), request);
        return this.doRequest(params, 'detail');
    }
    relatedContent(request) {
        request = lodash.get(request, 'probe.documentId') ? Object.assign(Object.assign({}, request), { probe: { documentId: lodash.get(request, 'probe.documentId') } }) : request;
        const params = Object.assign(Object.assign({}, this.getDefaultRequestParams()), request);
        return this.doRequest(params, 'relatedContent');
    }
    metatags(request = {}) {
        return (this.getApiService().invoke('query', 'metatags', { data: request }) || {});
    }
    discoverFields(request = {}) {
        return (this.getApiService().invoke('query', 'discoverFields', { data: request }) || {});
    }
    async addRule(type, docId) {
        if (type === 'globalBlock') {
            return (this.getApiService().invoke('rules', 'createRule', { data: { type, docId } }));
        }
        if (this.currentParams.query !== '*:*') {
            return (this.getApiService().invoke('rules', 'createRule', { data: { query: this.currentParams.query, type, docId } }));
        }
    }
    async getRulesByQuery() {
        return (this.getApiService().invoke('rules', 'getRulesByQuery', { data: { query: this.currentParams.query } }));
    }
    async getDoc(docId, fields, disableGlobalBlocks = true) {
        return (this.getApiService().invoke('query', 'detail', { data: Object.assign(Object.assign(Object.assign(Object.assign({}, lodash.omit(this.currentParams, 'queryId')), { id: docId }), (fields && { fieldList: fields })), { disableGlobalBlocks }) }));
    }
    async removeRule(rule) {
        return (this.getApiService().invoke('rules', 'deleteRule', { pathParams: { ruleId: lodash.get(rule, 'id') }, headers: { 'If-Match': `"${rule.version}"` } }));
    }
    async getQueriesWithAction() {
        return (this.getApiService().invoke('rules', 'getAllRuleQueries'));
    }
    async getGlobalRules() {
        return (this.getApiService().invoke('rules', 'getGlobalRules'));
    }
    /**
     * https://lucidworks.atlassian.net/wiki/spaces/SIN/pages/1619722481/Sitesearch+Zone+and+Template+Types
     *
     * Params for 'search-results' and 'typeahead':
     *
     * {
     *   "query": "query terms here",
     *   "page": 0,
     *
     *   "filters": [
     *     {"field": "extension", "value": "pdf"},
     *     {"field": "languageCode", "value": "en_US"},
     *     {"field": "datasourceTag", "value": "tag1"}
     *   ]
     * }
     *
     * Param for 'detail'
     *
     * {
     *   "id": "doc-id",
     * }
     *
     */
    async request(params, updateIndex) {
        let response;
        this.setActionParams(params);
        response = await this.doRequest();
        this.setQueryId(response.queryId);
        if (this.config.storeDataInZones) {
            this.setZoneStores(response || {}, updateIndex);
        }
        else {
            this.data = response;
        }
        this.notifyUpdates();
        return response;
    }
    /**
     * @override
     */
    notifyUpdatesOnSubscribe(callback) {
        if (this.loaded) {
            super.notifyUpdatesOnSubscribe(callback);
        }
    }
    async doRequest(params = this.currentParams, queryType = this.queryType) {
        if (queryType === 'search' && (lodash.isEmpty(params.query) || params.query === '*:*')) {
            return (this.getApiService().invoke('query', 'browse', { data: lodash.omit(params, ['query', 'highlight', 'queryId']), headers: this.config.headers }) || {});
        }
        return (this.getApiService().invoke('query', queryType, { data: lodash.omit(params, 'queryId'), headers: this.config.headers }) || {});
    }
    setActionParams(params) {
        const supportedParams = this.requestParamsByType[this.queryType];
        const newParams = lodash.pick(params, supportedParams);
        /**
         * For updating the `filters` param in `this.currentParams.filters`:
         *
         * If the new filters param contains a single facet, toggle this facet in `this.currentParams.filters`.
         * If the new filters contains multiple facets or it's empty, sync up `this.currentParams.filters` with it.
         */
        const toggleFacet = lodash.size(newParams.filters) === 1 && lodash.first(newParams.filters);
        if (toggleFacet) {
            // This also un-toggles the existing facet value from `this.currentParams.filters`.
            const filterToRemove = {
                field: toggleFacet.field,
                values: lodash.isArray(toggleFacet.values) ? toggleFacet.values : [toggleFacet.values]
            };
            const isDeselect = !lodash(this.currentParams.filters).remove(filterToRemove).isEmpty();
            // Remove all existing facet values for the same facet as render API only supports ANDs within facets.
            lodash.remove(this.currentParams.filters, { field: toggleFacet.field });
            if (!isDeselect) {
                this.currentParams.filters = [...(this.currentParams.filters || []), toggleFacet];
            }
        }
        else if (lodash.has(newParams, 'filters')) {
            this.currentParams.filters = lodash.isEmpty(newParams.filters) ? [] : newParams.filters;
        }
        // Overwrite current params from `newParams` except `filters` which has been updated by above `toggleFacet` handling.
        this.currentParams = Object.assign(Object.assign({}, this.currentParams), lodash.omit(newParams, 'filters'));
        // Reset to the first page when changing query or filters.
        if (lodash.intersection(['query', 'filters'], lodash.keys(params)).length && lodash.isUndefined(params.page)) {
            this.currentParams.page = 0;
        }
        this.validateParams();
    }
    /**
     * Validate query params and fallback to defaults for invalid values.
     */
    validateParams() {
        // Validate the sort params:
        // - Clear the sort param if the sort field doesn't exist in the defined map.
        // - Sort order should be either `asc` or `desc`, or it will fallback to `asc`.
        if (!lodash.isEmpty(this.currentParams.sort)) {
            const sortOption = this.currentParams.sort[0];
            if (lodash.includes(lodash.values(getSortFieldMap()), sortOption.sortField)) {
                sortOption.sortOrder = lodash.includes(['asc', 'desc'], sortOption.sortOrder) ? sortOption.sortOrder : 'asc';
            }
            else {
                delete this.currentParams.sort;
            }
        }
    }
    setZoneStores(response, updateIndex) {
        var _a;
        const zones = [];
        if (this.queryType === 'detail') {
            zones.push({ zoneType: 'detail', doc: { fields: response.fields, type: response.type }, datasourceLabels: response.datasourceLabels, id: response.id });
        }
        else {
            if (!lodash.isEmpty(response.facets)) {
                const facets = lodash.cloneDeep(response.facets);
                lodash.forEach((_a = response.facets) === null || _a === void 0 ? void 0 : _a.field, (v, k) => {
                    if (lodash.isEmpty(v.counts)) {
                        delete facets.field[k];
                    }
                });
                zones.push({ facets, zoneType: 'facets' });
            }
            if (!lodash.isEmpty(response.docs)) {
                const rows = this.currentParams.limit || 20;
                const start = rows * (this.currentParams.page || 0);
                const end = start + rows - 1;
                const pagination = { start, end };
                zones.push({ docs: response.docs, hits: response.hits, zoneType: this.queryType === 'searchahead' ? 'typeahead' : 'results-list', pagination });
            }
        }
        if (lodash.isEmpty(zones)) {
            this.data = [];
        }
        else {
            if (!lodash.isEmpty(this.data) && lodash.size(this.data) !== lodash.size(zones)) {
                this.data = [];
            }
            lodash.each(zones, (zone, index) => {
                if (!this.data[index]) {
                    this.data[index] = new ZoneStore(zone, {
                        index,
                        params: this.currentParams,
                        onAction: lodash.bind(this.onZoneAction, this),
                    });
                }
                else if (lodash.isUndefined(updateIndex) || updateIndex === index) {
                    this.data[index].setData(zone, lodash.cloneDeep(this.currentParams));
                }
            });
        }
    }
    /**
     * Only refresh the zone if the action is specific to the zone itself, or do global refresh.
     */
    onZoneAction(meta) {
        var _a;
        if (meta === null || meta === void 0 ? void 0 : meta.filters) {
            lodash.each(meta.filters, (filter) => {
                if (!lodash.isEmpty(filter.value) && lodash.isEmpty(filter.values)) {
                    filter.values = lodash.flatten([filter.value]);
                    delete filter.value;
                }
            });
        }
        const noneGlobalActions = ['sort', 'page'];
        const zoneToUpdate = !lodash(meta).pick(noneGlobalActions).isEmpty() && meta.store;
        const indexToUpdate = (_a = zoneToUpdate.meta) === null || _a === void 0 ? void 0 : _a.config.index;
        if (lodash.isString(meta === null || meta === void 0 ? void 0 : meta.sort)) {
            const field = meta === null || meta === void 0 ? void 0 : meta.sort;
            const createdField = 'document.sourceCreateTimestamp';
            const sortField = getSortFieldMap()[field] || createdField;
            const sortOrder = lodash.get(getSortOrderMap(), field, 'desc');
            meta = Object.assign(Object.assign({}, meta), { sort: [{ sortField, sortOrder }] });
        }
        this.request(meta, indexToUpdate);
    }
    getDefaultRequestParams() {
        var _a, _b;
        const analyticsData = lodash.isBoolean((_a = this.config.queryParams) === null || _a === void 0 ? void 0 : _a.analyticsData) ? (_b = this.config.queryParams) === null || _b === void 0 ? void 0 : _b.analyticsData : true;
        return Object.assign({ utcOffset: MoDatesService.getUtcOffset(), sessionId: getOrCreateSession() }, analyticsData === false && { analyticsData: false });
    }
}

const agentSearchData = {
    "queryId": "239bdd66-5ac9-42d8-bfa7-0cd88f8db32a",
    "hits": 40,
    docs: [{
            "type": "knowledgeArticle",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-KNOWLEDGEARTICLE-360049891273",
            "score": 1.3422422,
            "datasourceLabels": [],
            "fields": {
                "document.title": "How to Request a Health Check",
                "document.url": "https://support.lucidworks.com/hc/en-us/articles/360049891273-How-to-Request-a-Health-Check",
                "document.sourceLastModifiedTimestamp": "2020-09-03T17:11:42Z",
                "object.connectorType": "zendesk_connector",
                "knowledgeArticle.articleId": "360049891273"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-462",
            "score": 1.4800448,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Health check for lowes.com",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/462",
                "document.sourceLastModifiedTimestamp": "2019-10-30T17:39:10Z",
                "document.description": "original_ticket_id_5002E00001PLvqXQAT\n\nTeam, after meeting with Lowe's and Tim Potter, we have determined that a health check will be valuable for their Fusion 3.1.3 environment on Lowes.com and LowesForPros.com. Can you please initiate this health check and let me know what else you might need? The customer is looking to ensure holiday readiness and a future upgrade to 4.2.x. ----",
                "case.createdDate": "2019-08-07T11:13:29Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "462",
                "case.agentName": "Data Migration Agent"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-1271",
            "score": 1.2773302,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Health check for lowes.com and lowesforpros.com",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/1271",
                "document.sourceLastModifiedTimestamp": "2019-08-07T13:09:03Z",
                "document.description": "original_ticket_id_5002E00001PLvqrQAD\n\nTeam, after meeting with Lowe's and Tim Potter, we have determined that a health check will be valuable for their Fusion 3.1.3 environment on Lowes.com and LowesForPros.com. Can you please initiate this health check and let me know what else you might need? The customer is looking to ensure holiday readiness and a future upgrade to 4.2.x. ----",
                "case.createdDate": "2019-08-07T11:14:10Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "1271",
                "case.agentName": "Data Migration Agent"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-10386",
            "score": 1.2746005,
            "datasourceLabels": [],
            "fields": {
                "document.title": "[IST] Health Check Request ",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/10386",
                "document.sourceLastModifiedTimestamp": "2021-08-03T16:57:32Z",
                "document.description": "Hi,  \r\nplease help us to perform Health check for below points\r\n\r\n1. Problems we faced with Lenovo FUSION:\r\n\r\n•\tSpark nodes not coming up \r\n•\tHigh resource usage of Classic rest-service \r\n•\tnot able to start the jobs \r\n•\tPods were in pending state \r\n•\tSolr Shards went down ",
                "case.createdDate": "2021-06-04T05:18:39Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "10386"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-1180",
            "score": 1.2648132,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Health Check",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/1180",
                "document.sourceLastModifiedTimestamp": "2019-09-23T20:40:50Z",
                "document.description": "original_ticket_id_5002E00001PcZmRQAV\n\nWe'd like a Health Check on our current setup to know if things need to be changed. Our architecture has changed from the initial setup we had with Lucidworks. Some of our servers are in a DMZ zone. We'd also like to know the proper way to promote changes from our dev to our test environment. ----",
                "case.createdDate": "2019-08-20T17:47:39Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "1180",
                "case.agentName": "Mark Bennett"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-2964",
            "score": 1.2601535,
            "datasourceLabels": [],
            "fields": {
                "document.title": "{Feb/2019} Health Check Request",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/2964",
                "document.sourceLastModifiedTimestamp": "2019-03-13T02:30:08Z",
                "document.description": "original_ticket_id_5002E00001Irl8mQAB\n\nWe would like to perform a health check as we are running into lots of issues related to performance and cache replication.\n\nPlease find the details attached. ----",
                "case.createdDate": "2019-02-04T07:18:12Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "2964",
                "case.agentName": "Sourjyakanti Mohanty"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-4685",
            "score": 1.2572819,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Health check for lowes.com - GCP",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/4685",
                "document.sourceLastModifiedTimestamp": "2019-12-16T13:42:28Z",
                "document.description": "original_ticket_id_5002E00001RYmtBQAT\n\nTeam, after meeting with Lowe's and Tim Potter, we have determined that a health check will be valuable for their Fusion 3.1.3 environment on Lowes.com and LowesForPros.com. Can you please initiate this health check and let me know what else you might need? The customer is looking to ensure holiday readiness and a future upgrade to 4.2.x. ----",
                "case.createdDate": "2019-10-29T15:54:38Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "4685",
                "case.agentName": "Mark Bennett"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-2503",
            "score": 1.2521832,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Scheduling for Health Check",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/2503",
                "document.sourceLastModifiedTimestamp": "2019-12-06T03:56:42Z",
                "document.description": "original_ticket_id_5002E00001Pf5HsQAJ\n\nHi,\n\nWe would like to ask for Health Check to our production servers.\nCan we arrange that? What are the things we need to prepare?\n\nThank you.\n\nRegards,\nCalvin ----",
                "case.createdDate": "2019-08-28T05:58:28Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "2503",
                "case.agentName": "Data Migration Agent"
            }
        }, {
            "type": "page",
            "id": "3f923335-f2e1-4ced-ad64-4000541732db-13b6a161-a2f0-4816-9107-52aa51ede5de-31860711387440951764148880705110521567",
            "score": 1.2420344,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Product Features",
                "document.url": "https://academy.lucidworks.com/page/product-features",
                "object.connectorType": "web-connector",
                "document.description": "These courses offer a variety of product specific information to help you succeed"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-3588",
            "score": 1.2370166,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Need to do Health Check up & performance review",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/3588",
                "document.sourceLastModifiedTimestamp": "2019-03-15T05:13:27Z",
                "document.description": "original_ticket_id_5002E00001I6ve2QAB\n\nwe are creating new collections & servers to resolve existing issues related to performance.\nNeed Fusion support review & help for this ----",
                "case.createdDate": "2019-01-02T14:58:45Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "3588",
                "case.agentName": "Sourjyakanti Mohanty"
            }
        }, {
            "type": "knowledgeArticle",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-KNOWLEDGEARTICLE-360050733634",
            "score": 1.2369269,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Knowing your Zookeeper and checking on it’s health",
                "document.url": "https://support.lucidworks.com/hc/en-us/articles/360050733634-Knowing-your-Zookeeper-and-checking-on-it-s-health",
                "document.sourceLastModifiedTimestamp": "2020-07-07T22:20:21Z",
                "object.connectorType": "zendesk_connector",
                "knowledgeArticle.articleId": "360050733634"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-1995",
            "score": 1.2358682,
            "datasourceLabels": [],
            "fields": {
                "document.title": "“{05/2019}  Health Check Request",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/1995",
                "document.sourceLastModifiedTimestamp": "2019-08-21T17:28:11Z",
                "document.description": "original_ticket_id_5002E00001NR7P6QAL\n\nGet the environment reviewed and to get recommendations to optimize the usage ----",
                "case.createdDate": "2019-05-30T20:03:39Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "1995",
                "case.agentName": "Mark Bennett"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-2016",
            "score": 1.2357266,
            "datasourceLabels": [],
            "fields": {
                "document.title": "MAY/2019 Health Check Request",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/2016",
                "document.sourceLastModifiedTimestamp": "2019-06-27T08:33:25Z",
                "document.description": "original_ticket_id_5002E00001NREUFQA5\n\nWe are requesting a health check for an upcoming deployment.\n\nAttached are all files required for requesting a health check. Note that we included only one (1) config set. This is because we are migrating from one Fusion app (EMG; where there are a lot of collections with more or less the some configuration) to another Fusion app (erdee-nieuw; where we reduced the number of collections). At this moment we have three collections that share one config set. ----",
                "case.createdDate": "2019-05-31T07:44:39Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "2016",
                "case.agentName": "Sourjyakanti Mohanty"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-4957",
            "score": 1.2330878,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Request for Health Check on our brand new Fusion 4.1.0 stack in prod",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/4957",
                "document.sourceLastModifiedTimestamp": "2019-04-16T04:12:45Z",
                "document.description": "original_ticket_id_5002E00001JejXxQAJ\n\nHi there,\n\nWe are getting so close to wrap up our Fusion upgrade from 2.4 to 4.1.0. We have provisioned a new 4.1 stack with 3 Fusion app nodes and 3 zk nodes in AWS.\n\nI'm writing to you to request for a health check before we launch in prod.\n\nPlease let me know what info you need from my end.\n\nThanks,\nLisa ----",
                "case.createdDate": "2019-03-22T20:00:05Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "4957",
                "case.agentName": "Data Migration Agent"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-4812",
            "score": 1.2328918,
            "datasourceLabels": [],
            "fields": {
                "document.title": "10/2019 Health Check Request",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/4812",
                "document.sourceLastModifiedTimestamp": "2019-12-10T05:55:57Z",
                "document.description": "original_ticket_id_5002E00001RYK4LQAX\n\nReason for the health check:\n1) Many stability issues (like 26862 26866 26972 26835)\n2) Many slowness in production (related to some of those cases)\n3) Holiday period (which mean an increase of sale, increase of web traffic) is coming\n\nCheck the file HealthCheck.docx or HealthCheck.pdf to see where and how every output of the requested items are located. ----",
                "case.createdDate": "2019-10-28T14:56:42Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "4812",
                "case.agentName": "Data Migration Agent"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-5107",
            "score": 1.23181,
            "datasourceLabels": [],
            "fields": {
                "document.title": "04/2020 - Health Check Request (Dev)",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/5107",
                "document.sourceLastModifiedTimestamp": "2020-07-25T16:02:40Z",
                "document.description": "original_ticket_id_5002E00001YzWAnQAN\n\nThis health check is needed due to performance issues as well as a need to better understand capacity planning and capacity risks, i.e., is there anything we should be concerned about as we grow the use of Fusion with other parts of UHG.\n\nWe need both our STAGE and PROD environments evaluated. I'm attaching health check docs for both environments. ----",
                "case.createdDate": "2020-04-24T20:59:37Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "5107",
                "case.agentName": "Girish Jammu"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-1954",
            "score": 1.2253551,
            "datasourceLabels": [],
            "fields": {
                "document.title": "HealthCheck",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/1954",
                "document.sourceLastModifiedTimestamp": "2019-08-29T19:08:57Z",
                "document.description": "original_ticket_id_5002E00001P4NRIQA3\n\nI would like to initiate a health check ----",
                "case.createdDate": "2019-08-05T13:11:35Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "1954",
                "case.agentName": "Gitanjali Palwe"
            }
        }, {
            "type": "page",
            "id": "3f923335-f2e1-4ced-ad64-4000541732db-13b6a161-a2f0-4816-9107-52aa51ede5de-42377809075634085331962380136102963735",
            "score": 1.2176249,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Grocery Ecommerce Solutions - Lucidworks",
                "document.url": "https://lucidworks.com/product-discovery/industries/grocery-ecommerce/",
                "object.connectorType": "web-connector",
                "document.description": "Deliver the product selections and freshness that shoppers want, when they want them."
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-4376",
            "score": 1.216963,
            "datasourceLabels": [],
            "fields": {
                "document.title": "DS Mirror Health check plugin issue",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/4376",
                "document.sourceLastModifiedTimestamp": "2019-05-08T15:44:49Z",
                "document.description": "original_ticket_id_5002E00001M23lkQAB\n\nHi Team,\nWe are not able to get the response (like HTTP/1.1 200 OK) from the cluster when we trigger the curl command to get the health status from the cluster (for each host in the cluster)\n\nExample Curl command\ncurl -s -k -i -u solr:xxxxxx--key ../centralmonitoring/certs/dsmirror/file.key.pem --cacert ../centralmonitoring/certs/dsmirror/solr-ssl.pem --cert ../centralmonitoring/certs/dsmirror/file.crt.pem https://ma4-idmsp-st05-lapp10.corp.apple.com:8985/healthcheck/resource/health/isHealthy\n\n\nThanks,\nRavi ----",
                "case.createdDate": "2019-05-07T13:41:57Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "4376",
                "case.agentName": "Greg Harris"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-863",
            "score": 1.2166431,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Schedule a health check for Solr Global Portal cluster",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/863",
                "document.sourceLastModifiedTimestamp": "2019-05-17T18:44:04Z",
                "document.description": "original_ticket_id_5002E00001LGrBRQA1\n\nWe would like to go for a health check for Solr Global Portal cluster. Please contact us and let us know how shall we proceed with it. ----",
                "case.createdDate": "2019-04-25T11:35:14Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "863",
                "case.agentName": "Mark Bennett"
            }
        },
        {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-5121",
            "score": 1.334919,
            "datasourceLabels": [],
            "fields": {
                "document.title": "04/2020 - Health Check Request-PROD",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/5121",
                "document.sourceLastModifiedTimestamp": "2020-07-25T16:02:40Z",
                "document.description": "original_ticket_id_5002E00001Z1UJ7QAN\n\nThis health check is needed due to performance issues as well as a need to better understand capacity planning and capacity risks, i.e., is there anything we should be concerned about as we grow the use of Fusion with other parts of UHG.\n\nPROD environment. ----",
                "case.createdDate": "2020-05-01T06:56:13Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "5121",
                "case.agentName": "Madhu Sinha"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-2016",
            "score": 1.3340766,
            "datasourceLabels": [],
            "fields": {
                "document.title": "MAY/2019 Health Check Request",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/2016",
                "document.sourceLastModifiedTimestamp": "2019-06-27T08:33:25Z",
                "document.description": "original_ticket_id_5002E00001NREUFQA5\n\nWe are requesting a health check for an upcoming deployment.\n\nAttached are all files required for requesting a health check. Note that we included only one (1) config set. This is because we are migrating from one Fusion app (EMG; where there are a lot of collections with more or less the some configuration) to another Fusion app (erdee-nieuw; where we reduced the number of collections). At this moment we have three collections that share one config set. ----",
                "case.createdDate": "2019-05-31T07:44:39Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "2016",
                "case.agentName": "Sourjyakanti Mohanty"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-459",
            "score": 1.3275281,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Mar 2019 Health Check Request",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/459",
                "document.sourceLastModifiedTimestamp": "2019-06-04T22:41:15Z",
                "document.description": "original_ticket_id_5002E00001JoevqQAB\n\nHealth check request for performance issues and upcoming solr7 deployment.\nFrom onsite day in Austin we discussed:\n1. Initiate Support Healthchecks (v6 and v7)\n2. Repro - all shards in recovery issue\na. Solr and ZK logs\nb. CPU/Memory utilization\n3. Repro - the few instances in recovery mode stopping indexing\na. Solr and ZK logs\nb. CPU/Memory utilization\n4. Heap\na. Cache sizes, eviction/hit ratios\nb. Proactively determine appropriate heap sizes\n5. DocValues\na. Utilization of docValues… opportunities\n6. GC Log analysis (part of health check)\n7. Replica types - Separate MIGs\na. TLOGS and PULLS (pulls are autoscaled)\n8. Shards.tolerant=true as an option?\na. Maybe biz reasons can't do this? THD to investigate.\n9. Scaling up issue:\na. ZK reporting a node as \"Active\" when it wasn't ready… getting 5xx\nb. PR: Follow up\nc. More data needed from THD\n10. Examine Warmup queries & counts (were out of date, so updated them; they may or may not have an issue here)\n11. Backup and restore\na. Backup is not working while content is being index\na. Deployments are complex because backup and restore are not working as expected?\n1. Not clear on the connection between deployments and backups. ----",
                "case.createdDate": "2019-03-27T15:54:53Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "459",
                "case.agentName": "Greg Harris"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-4957",
            "score": 1.3263974,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Request for Health Check on our brand new Fusion 4.1.0 stack in prod",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/4957",
                "document.sourceLastModifiedTimestamp": "2019-04-16T04:12:45Z",
                "document.description": "original_ticket_id_5002E00001JejXxQAJ\n\nHi there,\n\nWe are getting so close to wrap up our Fusion upgrade from 2.4 to 4.1.0. We have provisioned a new 4.1 stack with 3 Fusion app nodes and 3 zk nodes in AWS.\n\nI'm writing to you to request for a health check before we launch in prod.\n\nPlease let me know what info you need from my end.\n\nThanks,\nLisa ----",
                "case.createdDate": "2019-03-22T20:00:05Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "4957",
                "case.agentName": "Data Migration Agent"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-3718",
            "score": 1.3226122,
            "datasourceLabels": [],
            "fields": {
                "document.title": "10/2019 Health Check Request",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/3718",
                "document.sourceLastModifiedTimestamp": "2019-12-02T11:34:01Z",
                "document.description": "original_ticket_id_5002E00001Qtv97QAB\n\nHI,\nWe would like to request an health check of our Fusion installation. We have often performance issues, and different problems with solr, connectors and apis.\n\nI collected all the details based on this article https://support.lucidworks.com/s/article/How-to-Request-a-Health-Check, a part from:\n- point 10 \"Solr Config Files for Primary Data Collections\": when I try to run the command I get this error: \"Error downloading files from zookeeper path /configs/Booking to /home/fusion/conf\" .\nI'm calling it like this:\n/opt/fusion/4.0.1/apps/solr-dist/server/scripts/cloud-scripts/zkcli.sh -cmd downconfig -confname Booking -z LHR4-ESRCHAP-04:9983 -confdir /home/fusion/conf\nProbably I'm doing something wrong, but I'm not sure what.\n- point 11 \"Solr Index Size and Document Count\"I'm not sure where to find this info in the sorl dashboard.\n\nI also attached a month of logs from every node, I'm uploading all the info in the ftp server.\n\nNode LHR4-ESRCHAP-02 is currently down and I raised a separate ticket to fix it https://support.lucidworks.com/s/case/5002E00001QtqQvQAJ/lhr4escrhap02-fails-to-start\n\nPlease let me know if anything is missing a part from those 2 things.\n\nCheers\nGraziella ----",
                "case.createdDate": "2019-10-08T15:27:49Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "3718",
                "case.agentName": "Shubhashree  Rudramunaiah"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-2971",
            "score": 1.32139,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Perform system Health Check",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/2971",
                "document.sourceLastModifiedTimestamp": "2019-07-12T16:57:43Z",
                "document.description": "original_ticket_id_5002E00001Ni19pQAB\n\nWe would like to perform a system health check for the dev and prod environments of OReilly.com.\n\nThey are having various issues including clusters collapsing intermittently. ----",
                "case.createdDate": "2019-06-13T19:00:01Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "2971",
                "case.agentName": "Gitanjali Palwe"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-6334",
            "score": 1.3138354,
            "datasourceLabels": [],
            "fields": {
                "document.title": "SQL BAD status ",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/6334",
                "document.sourceLastModifiedTimestamp": "2020-11-09T22:02:20Z",
                "document.description": "Hi \r\n\r\nthe SQL Health in  DevOps Center is bad \r\n\r\nwe have 4 fusion servers mean that we should have 4 SQL services.\r\n\r\nsomehow one of the nodes  have 2 SQL services - 1 is ok and the second is FAILED \r\n\r\n\r\n\r\n",
                "case.createdDate": "2020-10-08T11:59:53Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "6334",
                "case.agentName": "Jonathan Martin"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-1787",
            "score": 1.3098632,
            "datasourceLabels": [],
            "fields": {
                "document.title": "{Mar/2020} Health Check Request",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/1787",
                "document.sourceLastModifiedTimestamp": "2020-06-18T13:32:45Z",
                "document.description": "original_ticket_id_5002E00001PX7eHQAT\n\nI will try to find the architecture document too for the project and the hardware documents as well. Please let me know what specifically is required document for these two in health check. I have listed out general hardware configurations below too if needed. Attached are files as requested for Prod fusion health check in the 7Zip document:\nzookeeper node 1 status:\nZooKeeper JMX enabled by default\nUsing config: /opt/zookeeper/bin/../conf/zoo.cfg\nMode: follower\nzookeeper node 2:\nZooKeeper JMX enabled by default\nUsing config: /opt/zookeeper/bin/../conf/zoo.cfg\nMode: leader\nzookeeper node 3 status:\nZooKeeper JMX enabled by default\nUsing config: /opt/zookeeper/bin/../conf/zoo.cfg\nMode: follower\n\n\nHardware:\nNumber of nodes: 3\n\nShards in relevant collection: 3\n\nReplicas in relevant collection: 3\n\nCPU physical cores in each node: 16\n\nRAM:64 gb FREE:~7 GB\n\nDisk: 600Gb free: ~500Gb ----",
                "case.createdDate": "2020-03-27T11:54:29Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "1787",
                "case.agentName": "Shubhashree  Rudramunaiah"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-6350",
            "score": 1.3084419,
            "datasourceLabels": [],
            "fields": {
                "document.title": "TDA Health check 2020",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/6350",
                "document.sourceLastModifiedTimestamp": "2021-01-19T21:01:13Z",
                "document.description": "This is a healthcheck case that should be assigned to Greg Harris\r\n\r\nGreg has directed us on gathering proper data for the health check. I'm setting up the ticket so we can add data to the case for Greg to access. \r\n\r\nThanks, \r\n\r\nCharles, ",
                "case.createdDate": "2020-10-09T16:06:51Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "6350",
                "case.agentName": "Greg Harris"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-1295",
            "score": 1.3018272,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Health check and KT",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/1295",
                "document.sourceLastModifiedTimestamp": "2019-02-27T08:06:36Z",
                "document.description": "original_ticket_id_5002E00001J2NU9QAN\n\nThis is a request for health check and KT ----",
                "case.createdDate": "2019-02-26T20:31:12Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "1295",
                "case.agentName": "Data Migration Agent"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-3588",
            "score": 1.3007388,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Need to do Health Check up & performance review",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/3588",
                "document.sourceLastModifiedTimestamp": "2019-03-15T05:13:27Z",
                "document.description": "original_ticket_id_5002E00001I6ve2QAB\n\nwe are creating new collections & servers to resolve existing issues related to performance.\nNeed Fusion support review & help for this ----",
                "case.createdDate": "2019-01-02T14:58:45Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "3588",
                "case.agentName": "Sourjyakanti Mohanty"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-277",
            "score": 1.3000059,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Health check and KT",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/277",
                "document.sourceLastModifiedTimestamp": "2019-03-12T19:05:41Z",
                "document.description": "original_ticket_id_5002E00001J2NU4QAN\n\nThis is a request for health check and KT ----",
                "case.createdDate": "2019-02-26T20:31:08Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "277",
                "case.agentName": "Gitanjali Palwe"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-1329",
            "score": 1.2974175,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Health check and general tuning review (for Diesel)",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/1329",
                "document.sourceLastModifiedTimestamp": "2019-07-26T20:33:43Z",
                "document.description": "original_ticket_id_5002E00001O51k7QAB\n\nDiesel has several networks: one unclassified, others which are classified. They have been having general stability issues and systems crashing. I have been working with them to troubleshoot, but with restricted access to log files, it is difficult.\n\nIf someone from support can reach out and schedule a general health check, that would be terrific. This should be US citizen only, and be aware there are strict rules on what they can share vs not share. Also no screen sharing, audio only for zoom. ----",
                "case.createdDate": "2019-06-20T16:07:56Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "1329",
                "case.agentName": "Greg Harris"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-3406",
            "score": 1.2848437,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Perform System Health Check for Upgrade",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/3406",
                "document.sourceLastModifiedTimestamp": "2019-07-15T18:17:00Z",
                "document.description": "original_ticket_id_5002E00001NR3Z7QAL\n\nWe are trying to move OReillys upgrade into production. Sean O'Connor is seeing major issues with the speed of Fusion, Fusion overloading a crashing, we would like support to help us review the install and perform a system health check. This is very critical as it is part of what is blocking up from moving into production. ----",
                "case.createdDate": "2019-05-30T17:26:35Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "3406",
                "case.agentName": "Gitanjali Palwe"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-6121",
            "score": 1.275912,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Health Check for TD Ameritrade V4.2.3",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/6121",
                "document.sourceLastModifiedTimestamp": "2020-10-25T20:02:55Z",
                "document.description": "Good Afternoon Support,\n\nHope all is well. TD has been experiencing some production issues and has requested a health check. Can you assist me in getting this process started? Thanks!\n\nDan Britman\nCustomer Success Director",
                "case.createdDate": "2020-09-17T20:13:18Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "6121",
                "case.agentName": "Greg Harris"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-6296",
            "score": 1.2758899,
            "datasourceLabels": [],
            "fields": {
                "document.title": "10/2020 Health Check Request",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/6296",
                "document.sourceLastModifiedTimestamp": "2020-11-22T03:02:13Z",
                "document.description": "Hello,\r\nWe would like to perform a health check because the last month we had fusion outages and zookeepers corruptions.\r\nAttaching the requested artifacts to the ticket. \r\nI couldn't get artifact 10 because I had an error about the config directory not existing.\r\n\r\nThank you in advance.\r\n\r\nBest Regards,\r\nMati.\r\n\r\nThis ticket includes a secure attachment. Use this link to access the attached files:\r\n https://send.lucidworks.com/receive/?thread=YAEW-XTPV&packageCode=V0K0pvW6yH7ID0b1FZdwL9n2nlGSGL0z4jckQAf2oFo#keyCode=1YFm5XW5U0KwtUsD6-UcI66pWLUjyCtxjuAVofUz8E4",
                "case.createdDate": "2020-10-05T21:30:51Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "6296",
                "case.agentName": "Rakesh Maski"
            }
        }, {
            "type": "knowledgeArticle",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-KNOWLEDGEARTICLE-360050733634",
            "score": 1.2744024,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Knowing your Zookeeper and checking on it’s health",
                "document.url": "https://support.lucidworks.com/hc/en-us/articles/360050733634-Knowing-your-Zookeeper-and-checking-on-it-s-health",
                "document.sourceLastModifiedTimestamp": "2020-07-07T22:20:21Z",
                "object.connectorType": "zendesk_connector",
                "knowledgeArticle.articleId": "360050733634"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-4631",
            "score": 1.2743098,
            "datasourceLabels": [],
            "fields": {
                "document.title": "03/2019 Health Check Request",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/4631",
                "document.sourceLastModifiedTimestamp": "2019-11-29T06:39:42Z",
                "document.description": "original_ticket_id_5002E00001JLLbCQAX\n\nWe have been experiencing periodic API errors in the Fusion console. In this case we need to restart the Fusion instance. This occurs most frequently in our staging environment were we have more Fusion collections (multiple instances of collections matching our production environment).\nOur concern is that this may impact our production content indexing.\n\nAt this time I am submitting the data for the Staging environment. I will submit another request for our production environment as a second request.\n\nNote: The compressed logs listed in the page https://support.lucidworks.com/s/article/How-to-Request-a-Health-Check are around 290 MB which is greater than the 25 MB upload limit for this web site. ----",
                "case.createdDate": "2019-03-04T16:03:06Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "4631",
                "case.agentName": "Gitanjali Palwe"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-1212",
            "score": 1.272992,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Requesting a Health Check for IT-Marketing's implementation of LW Fusion",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/1212",
                "document.sourceLastModifiedTimestamp": "2020-06-01T21:31:09Z",
                "document.description": "original_ticket_id_5002E00001UPZvMQAX\n\nI believe another group from Red Hat has requested this in past and it is a service you offer us. I would like to know more about what is standard and what we have concerns about regarding our implementation. Perhaps we could set up a video chat the first week of the New Year to discuss what would be involved.\n\nThank you,\nJeff ----",
                "case.createdDate": "2019-12-19T21:06:09Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "1212",
                "case.agentName": "Gitanjali Palwe"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-9930",
            "score": 1.2694404,
            "datasourceLabels": [],
            "fields": {
                "document.title": "April 2021 Health Check Request",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/9930",
                "document.sourceLastModifiedTimestamp": "2021-07-21T19:02:42Z",
                "document.description": "request for  health check to optimize performance. \r\n\r\nThis ticket includes a secure attachment. Use this link to access the attached files:\r\n https://send.lucidworks.com/receive/?thread=NKT2-PGRK&packageCode=hWVmhA4QQVXxJQ0DfmgHwGO11oYa42AFwY4nTI779Zk#keyCode=Ewotqk6_F-i2zKgq7ROFe6X6GxbRsbjjn8cZxh0mg1g",
                "case.createdDate": "2021-04-26T17:49:42Z",
                "object.connectorType": "zendesk_connector",
                "case.caseId": "9930",
                "case.agentName": "Greg Harris"
            }
        }]
};
const similarCases = {
    "queryId": "e7a0a567-b343-455d-904d-f1ac7b4259fd",
    "hits": 39,
    "docs": [{
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-462",
            "score": 1.9885811,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Health check for lowes.com",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/462",
                "document.description": "original_ticket_id_5002E00001PLvqXQAT\n\nTeam, after meeting with Lowe's and Tim Potter, we have determined that a health check will be valuable for their Fusion 3.1.3 environment on Lowes.com and LowesForPros.com. Can you please initiate this health check and let me know what else you might need? The customer is looking to ensure holiday readiness and a future upgrade to 4.2.x. ----",
                "case.createdDate": "2019-08-07T11:13:29Z",
                "case.caseId": "462",
                "case.agentName": "Data Migration Agent"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-1271",
            "score": 1.9749582,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Health check for lowes.com and lowesforpros.com",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/1271",
                "document.description": "original_ticket_id_5002E00001PLvqrQAD\n\nTeam, after meeting with Lowe's and Tim Potter, we have determined that a health check will be valuable for their Fusion 3.1.3 environment on Lowes.com and LowesForPros.com. Can you please initiate this health check and let me know what else you might need? The customer is looking to ensure holiday readiness and a future upgrade to 4.2.x. ----",
                "case.createdDate": "2019-08-07T11:14:10Z",
                "case.caseId": "1271",
                "case.agentName": "Data Migration Agent"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-6246",
            "score": 1.9570299,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Would like CASE plan and Health check of our Lucidworks Fusion environment",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/6246",
                "document.description": "Hello.  I would like to schedule CASE plan and Health check of our Lucidworks Fusion environment.  Looking for late October perhaps.",
                "case.createdDate": "2020-09-29T16:52:38Z",
                "case.caseId": "6246",
                "case.agentName": "Rakesh Maski"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-4685",
            "score": 1.950065,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Health check for lowes.com - GCP",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/4685",
                "document.description": "original_ticket_id_5002E00001RYmtBQAT\n\nTeam, after meeting with Lowe's and Tim Potter, we have determined that a health check will be valuable for their Fusion 3.1.3 environment on Lowes.com and LowesForPros.com. Can you please initiate this health check and let me know what else you might need? The customer is looking to ensure holiday readiness and a future upgrade to 4.2.x. ----",
                "case.createdDate": "2019-10-29T15:54:38Z",
                "case.caseId": "4685",
                "case.agentName": "Mark Bennett"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-5121",
            "score": 1.6654377,
            "datasourceLabels": [],
            "fields": {
                "document.title": "04/2020 - Health Check Request-PROD",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/5121",
                "document.description": "original_ticket_id_5002E00001Z1UJ7QAN\n\nThis health check is needed due to performance issues as well as a need to better understand capacity planning and capacity risks, i.e., is there anything we should be concerned about as we grow the use of Fusion with other parts of UHG.\n\nPROD environment. ----",
                "case.createdDate": "2020-05-01T06:56:13Z",
                "case.caseId": "5121",
                "case.agentName": "Madhu Sinha"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-5107",
            "score": 1.6653645,
            "datasourceLabels": [],
            "fields": {
                "document.title": "04/2020 - Health Check Request (Dev)",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/5107",
                "document.description": "original_ticket_id_5002E00001YzWAnQAN\n\nThis health check is needed due to performance issues as well as a need to better understand capacity planning and capacity risks, i.e., is there anything we should be concerned about as we grow the use of Fusion with other parts of UHG.\n\nWe need both our STAGE and PROD environments evaluated. I'm attaching health check docs for both environments. ----",
                "case.createdDate": "2020-04-24T20:59:37Z",
                "case.caseId": "5107",
                "case.agentName": "Girish Jammu"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-3588",
            "score": 1.9906601,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Need to do Health Check up & performance review",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/3588",
                "document.description": "original_ticket_id_5002E00001I6ve2QAB\n\nwe are creating new collections & servers to resolve existing issues related to performance.\nNeed Fusion support review & help for this ----",
                "case.createdDate": "2019-01-02T14:58:45Z",
                "case.caseId": "3588",
                "case.agentName": "Sourjyakanti Mohanty"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-4957",
            "score": 1.6463718,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Request for Health Check on our brand new Fusion 4.1.0 stack in prod",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/4957",
                "document.description": "original_ticket_id_5002E00001JejXxQAJ\n\nHi there,\n\nWe are getting so close to wrap up our Fusion upgrade from 2.4 to 4.1.0. We have provisioned a new 4.1 stack with 3 Fusion app nodes and 3 zk nodes in AWS.\n\nI'm writing to you to request for a health check before we launch in prod.\n\nPlease let me know what info you need from my end.\n\nThanks,\nLisa ----",
                "case.createdDate": "2019-03-22T20:00:05Z",
                "case.caseId": "4957",
                "case.agentName": "Data Migration Agent"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-2016",
            "score": 1.6256323,
            "datasourceLabels": [],
            "fields": {
                "document.title": "MAY/2019 Health Check Request",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/2016",
                "document.description": "original_ticket_id_5002E00001NREUFQA5\n\nWe are requesting a health check for an upcoming deployment.\n\nAttached are all files required for requesting a health check. Note that we included only one (1) config set. This is because we are migrating from one Fusion app (EMG; where there are a lot of collections with more or less the some configuration) to another Fusion app (erdee-nieuw; where we reduced the number of collections). At this moment we have three collections that share one config set. ----",
                "case.createdDate": "2019-05-31T07:44:39Z",
                "case.caseId": "2016",
                "case.agentName": "Sourjyakanti Mohanty"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-1212",
            "score": 1.6177841,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Requesting a Health Check for IT-Marketing's implementation of LW Fusion",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/1212",
                "document.description": "original_ticket_id_5002E00001UPZvMQAX\n\nI believe another group from Red Hat has requested this in past and it is a service you offer us. I would like to know more about what is standard and what we have concerns about regarding our implementation. Perhaps we could set up a video chat the first week of the New Year to discuss what would be involved.\n\nThank you,\nJeff ----",
                "case.createdDate": "2019-12-19T21:06:09Z",
                "case.caseId": "1212",
                "case.agentName": "Gitanjali Palwe"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-4661",
            "score": 1.5788603,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Health Check Request",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/4661",
                "document.description": "original_ticket_id_5002E00001PcVHSQA3\n\nSpectrum Health would like a Health Check of their current architecture and set up prior to expanding to additional use cases ----",
                "case.createdDate": "2019-08-20T15:16:15Z",
                "case.caseId": "4661",
                "case.agentName": "Data Migration Agent"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-3406",
            "score": 1.574251,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Perform System Health Check for Upgrade",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/3406",
                "document.description": "original_ticket_id_5002E00001NR3Z7QAL\n\nWe are trying to move OReillys upgrade into production. Sean O'Connor is seeing major issues with the speed of Fusion, Fusion overloading a crashing, we would like support to help us review the install and perform a system health check. This is very critical as it is part of what is blocking up from moving into production. ----",
                "case.createdDate": "2019-05-30T17:26:35Z",
                "case.caseId": "3406",
                "case.agentName": "Gitanjali Palwe"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-4631",
            "score": 1.5682713,
            "datasourceLabels": [],
            "fields": {
                "document.title": "03/2019 Health Check Request",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/4631",
                "document.description": "original_ticket_id_5002E00001JLLbCQAX\n\nWe have been experiencing periodic API errors in the Fusion console. In this case we need to restart the Fusion instance. This occurs most frequently in our staging environment were we have more Fusion collections (multiple instances of collections matching our production environment).\nOur concern is that this may impact our production content indexing.\n\nAt this time I am submitting the data for the Staging environment. I will submit another request for our production environment as a second request.\n\nNote: The compressed logs listed in the page https://support.lucidworks.com/s/article/How-to-Request-a-Health-Check are around 290 MB which is greater than the 25 MB upload limit for this web site. ----",
                "case.createdDate": "2019-03-04T16:03:06Z",
                "case.caseId": "4631",
                "case.agentName": "Gitanjali Palwe"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-4812",
            "score": 1.5563138,
            "datasourceLabels": [],
            "fields": {
                "document.title": "10/2019 Health Check Request",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/4812",
                "document.description": "original_ticket_id_5002E00001RYK4LQAX\n\nReason for the health check:\n1) Many stability issues (like 26862 26866 26972 26835)\n2) Many slowness in production (related to some of those cases)\n3) Holiday period (which mean an increase of sale, increase of web traffic) is coming\n\nCheck the file HealthCheck.docx or HealthCheck.pdf to see where and how every output of the requested items are located. ----",
                "case.createdDate": "2019-10-28T14:56:42Z",
                "case.caseId": "4812",
                "case.agentName": "Data Migration Agent"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-1180",
            "score": 1.5548474,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Health Check",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/1180",
                "document.description": "original_ticket_id_5002E00001PcZmRQAV\n\nWe'd like a Health Check on our current setup to know if things need to be changed. Our architecture has changed from the initial setup we had with Lucidworks. Some of our servers are in a DMZ zone. We'd also like to know the proper way to promote changes from our dev to our test environment. ----",
                "case.createdDate": "2019-08-20T17:47:39Z",
                "case.caseId": "1180",
                "case.agentName": "Mark Bennett"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-2964",
            "score": 1.5508969,
            "datasourceLabels": [],
            "fields": {
                "document.title": "{Feb/2019} Health Check Request",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/2964",
                "document.description": "original_ticket_id_5002E00001Irl8mQAB\n\nWe would like to perform a health check as we are running into lots of issues related to performance and cache replication.\n\nPlease find the details attached. ----",
                "case.createdDate": "2019-02-04T07:18:12Z",
                "case.caseId": "2964",
                "case.agentName": "Sourjyakanti Mohanty"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-1954",
            "score": 1.5356442,
            "datasourceLabels": [],
            "fields": {
                "document.title": "HealthCheck",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/1954",
                "document.description": "original_ticket_id_5002E00001P4NRIQA3\n\nI would like to initiate a health check ----",
                "case.createdDate": "2019-08-05T13:11:35Z",
                "case.caseId": "1954",
                "case.agentName": "Gitanjali Palwe"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-2032",
            "score": 1.5300238,
            "datasourceLabels": [],
            "fields": {
                "document.title": "01/2019 Ace Hardward Health Check Request",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/2032",
                "document.description": "original_ticket_id_5002E00001IAPSVQA5\n\nWe have never had a health check done before. We want to decrease the time of our crawls. We are also very interested in finding out what else the health check reveals and what other things we have the opportunity to improve.\n\nWe are in the process of adding the logs. ----",
                "case.createdDate": "2019-01-11T19:19:51Z",
                "case.caseId": "2032",
                "case.agentName": "Greg Harris"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-3718",
            "score": 1.5265229,
            "datasourceLabels": [],
            "fields": {
                "document.title": "10/2019 Health Check Request",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/3718",
                "document.description": "original_ticket_id_5002E00001Qtv97QAB\n\nHI,\nWe would like to request an health check of our Fusion installation. We have often performance issues, and different problems with solr, connectors and apis.\n\nI collected all the details based on this article https://support.lucidworks.com/s/article/How-to-Request-a-Health-Check, a part from:\n- point 10 \"Solr Config Files for Primary Data Collections\": when I try to run the command I get this error: \"Error downloading files from zookeeper path /configs/Booking to /home/fusion/conf\" .\nI'm calling it like this:\n/opt/fusion/4.0.1/apps/solr-dist/server/scripts/cloud-scripts/zkcli.sh -cmd downconfig -confname Booking -z LHR4-ESRCHAP-04:9983 -confdir /home/fusion/conf\nProbably I'm doing something wrong, but I'm not sure what.\n- point 11 \"Solr Index Size and Document Count\"I'm not sure where to find this info in the sorl dashboard.\n\nI also attached a month of logs from every node, I'm uploading all the info in the ftp server.\n\nNode LHR4-ESRCHAP-02 is currently down and I raised a separate ticket to fix it https://support.lucidworks.com/s/case/5002E00001QtqQvQAJ/lhr4escrhap02-fails-to-start\n\nPlease let me know if anything is missing a part from those 2 things.\n\nCheers\nGraziella ----",
                "case.createdDate": "2019-10-08T15:27:49Z",
                "case.caseId": "3718",
                "case.agentName": "Shubhashree  Rudramunaiah"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-7462",
            "score": 1.5229559,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Request:  System Health Check for Morgan Stanley",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/7462",
                "document.description": "Morgan Stanley has requested a health check of their environment due to some outages recently.",
                "case.createdDate": "2021-01-22T17:01:36Z",
                "case.caseId": "7462",
                "case.agentName": "Mark Bennett"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-2955",
            "score": 1.5218438,
            "datasourceLabels": [],
            "fields": {
                "document.title": "11/2019 Health Check Request",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/2955",
                "document.description": "original_ticket_id_5002E00001SO7y8QAD\n\nUpcoming upgrade from 4.1.2 to 4.2; interested in optimizing performance ----",
                "case.createdDate": "2019-11-19T19:56:15Z",
                "case.caseId": "2955",
                "case.agentName": "Doug McCoy"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-1787",
            "score": 1.520454,
            "datasourceLabels": [],
            "fields": {
                "document.title": "{Mar/2020} Health Check Request",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/1787",
                "document.description": "original_ticket_id_5002E00001PX7eHQAT\n\nI will try to find the architecture document too for the project and the hardware documents as well. Please let me know what specifically is required document for these two in health check. I have listed out general hardware configurations below too if needed. Attached are files as requested for Prod fusion health check in the 7Zip document:\nzookeeper node 1 status:\nZooKeeper JMX enabled by default\nUsing config: /opt/zookeeper/bin/../conf/zoo.cfg\nMode: follower\nzookeeper node 2:\nZooKeeper JMX enabled by default\nUsing config: /opt/zookeeper/bin/../conf/zoo.cfg\nMode: leader\nzookeeper node 3 status:\nZooKeeper JMX enabled by default\nUsing config: /opt/zookeeper/bin/../conf/zoo.cfg\nMode: follower\n\n\nHardware:\nNumber of nodes: 3\n\nShards in relevant collection: 3\n\nReplicas in relevant collection: 3\n\nCPU physical cores in each node: 16\n\nRAM:64 gb FREE:~7 GB\n\nDisk: 600Gb free: ~500Gb ----",
                "case.createdDate": "2020-03-27T11:54:29Z",
                "case.caseId": "1787",
                "case.agentName": "Shubhashree  Rudramunaiah"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-2389",
            "score": 1.5195816,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Fusion unstable in Prod - PRODUCTION OUTAGE",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/2389",
                "document.description": "original_ticket_id_5002E00001WosHTQAZ\n\nFiling case on behalf of the customer:\n\nClient has requested a call to debug, Lucidworks PS will be on the call:\n\nCall is scheduled Sunday, February 2, 2020 @ 8pm PST.\n\n\nHi Austin,\nAs we exchanged on slack, Bunnings fusion is currently out of LIVE traffic and here is some more info about the issue:\nThere are total 3 web machines which can talk to Fusion for search requests.\nThere is a toggle switch which can control traffic direction between Fusion and old solr.\nThere is a healthcheck request (called status check) is called every 30 seconds to check the health of web machines. This \"status\" check calls various search requests. And if the status is not returning SUCCESS in 30 seconds, then it marks those machine un-healthy.\nYesterday (Jan 30th, 2019 around 3PM Australia time) when they enabled traffic to fusion, things were fine for some time. But at around 3:55PM AU time they saw status healthcheck failing. The graphs indicate that there were high connections count on web servers. So, they moved these machines out of the traffic.\nWe also saw connection refused/connection reset errors in proxy jetty request logs.\n\n\nfusion@prod-fus-spa1:/opt/lucidworks/fusion/var/log/proxy$ head -100 proxy.log\n2020-01-30T04:53:35,203 - ERROR [qtp2008017533-194955:clojure.tools.logging$eval11$fn__16@0] - {} - Uncaught application error!\norg.apache.http.conn.HttpHostConnectException: Connect to 172.18.25.10:8765 [/172.18.25.10] failed: Connection refused (Connection refused)\n at org.apache.http.impl.conn.DefaultHttpClientConnectionOperator.connect(DefaultHttpClientConnectionOperator.java:159) ~[httpclient-4.5.6.jar:4.5.6]\n at org.apache.http.impl.conn.PoolingHttpClientConnectionManager.connect(PoolingHttpClientConnectionManager.java:373) ~[httpclient-4.5.6.jar:4.5.6]\n at org.apache.http.impl.execchain.MainClientExec.establishRoute(MainClientExec.java:394) ~[httpclient-4.5.6.jar:4.5.6]\n at org.apache.http.impl.execchain.MainClientExec.execute(MainClientExec.java:237) ~[httpclient-4.5.6.jar:4.5.6]\n at org.apache.http.impl.execchain.ProtocolExec.execute(ProtocolExec.java:185) ~[httpclient-4.5.6.jar:4.5.6]\nWe also saw queries getting 404\n\n172.18.12.164 - - [30/Jan/2020:04:54:19 +0000] \"GET /api/apps/pdb_app/query/buni_index_pdb_au_green?q=*%3a*&start=0&rows=500&fl=BmieCategoryName%2cboost%2cBundleProductGroups%2cBundleProductIds%2cBundleProducts%2cCategoryId%2cCategoryIdPath%2cCategoryNamePath%2cdocNumber%2cHasImage%2cId%2cImages%2cItemType%2cKeywords%2cPriceRange%2cProductTypeId%2cProductTypeKey%2cRelatedFineLines%2crelevance%2cRevision%2cServicePromotionId%2cServiceType%2cSortableName%2cSpellingFields%2cStoreType%2cTaskExecutionId%2cTeamMemberPrice%2cVariantFineLines%2cWebCategoryIdAndWebBrandId%2cId%2cDisplayName%2cKeywords%2cDescription%2cItemType%2cLocations%2cDateCreated%2cDateModified%2cActiveRegions%2cOtherType%2cIsHiddenFromListing%2cStoreType%2cRegion_wametro_Active%2cRegion_wametro_Boost&fq=(ItemType%3a((Diy)))&fq=Region_wametro_Active%3a(1)&fq=IsHiddenFromListing%3a(0)&version=2.2&wt=xml HTTP/1.1\" 404 20 84\n172.18.12.164 - - [30/Jan/2020:04:54:19 +0000] \"GET /api/apps/pdb_app/query/buni_index_pdb_au_green?q=*%3a*&rows=0&terms=true&terms.fl=AutoSuggest&terms.prefix=hafan&terms.limit=-1&terms.mincount=5&version=2.2&wt=xml HTTP/1.1\" 404 20 8\n172.18.12.164 - - [30/Jan/2020:04:54:19 +0000] \"GET /api/apps/pdb_app/query/buni_index_pdb_au_green?q=*%3a*&start=0&rows=500&fl=BmieCategoryName%2cboost%2cBundleProductGroups%2cBundleProductIds%2cBundleProducts%2cCategoryId%2cCategoryIdPath%2cCategoryNamePath%2cdocNumber%2cHasImage%2cId%2cImages%2cItemType%2cKeywords%2cPriceRange%2cProductTypeId%2cProductTypeKey%2cRelatedFineLines%2crelevance%2cRevision%2cServicePromotionId%2cServiceType%2cSortableName%2cSpellingFields%2cStoreType%2cTaskExecutionId%2cTeamMemberPrice%2cVariantFineLines%2cWebCategoryIdAndWebBrandId%2cId%2cDisplayName%2cKeywords%2cDescription%2cItemType%2cLocations%2cDateCreated%2cDateModified%2cActiveRegions%2cOtherType%2cIsHiddenFromListing%2cStoreType%2cRegion_vicmetro_Active%2cRegion_vicmetro_Boost&fq=(ItemType%3a((Service)))&fq=Region_vicmetro_Active%3a(1)&fq=IsHiddenFromListing%3a(0)&version=2.2&wt=xml HTTP/1.1\" 404 20 4\n172.18.25.12 - - [30/Jan/2020:04:54:20 +0000] \"HEAD /api/?ignore_log=true HTTP/1.1\" 200 402 223\n172.18.25.12 - - [30/Jan/2020:04:54:21 +0000] \"HEAD /api/?ignore_log=true HTTP/1.1\" 200 402 313\n172.18.25.22 - - [30/Jan/2020:04:54:21 +0000] \"GET /api/webapps/type/admin/configs?hidden=true&ignore_log=true HTTP/1.1\" 200 1560 118\n172.18.25.12 - - [30/Jan/2020:04:54:22 +0000] \"HEAD /api/?ignore_log=true HTTP/1.1\" 200 402 153\n172.18.25.12 - - [30/Jan/2020:04:54:23 +0000] \"HEAD /api/?ignore_log=true HTTP/1.1\" 200 402 137\n172.18.25.12 - - [30/Jan/2020:04:54:24 +0000] \"HEAD /api/?ignore_log=true HTTP/1.1\" 200 402 139\n172.18.25.12 - - [30/Jan/2020:04:54:26 +0000] \"HEAD /api/?ignore_log=true HTTP/1.1\" 200 402 106\n172.18.25.12 - - [30/Jan/2020:04:54:27 +0000] \"HEAD /api/?ignore_log=true HTTP/1.1\" 200 402 285\n172.18.25.12 - - [30/Jan/2020:04:54:28 +0000] \"HEAD /api/?ignore_log=true HTTP/1.1\" 200 402 403\n172.18.25.12 - - [30/Jan/2020:04:54:29 +0000] \"HEAD /api/?ignore_log=true HTTP/1.1\" 200 402 239\n\nWe also saw some errors in solr logs (related to commit and opening new searcher), we are not sure if this error is related or not.\n\n12:11\n2020-01-30T04:51:40,954 - INFO [qtp1127224355-3642:update.processor.LogUpdateProcessorFactory$LogUpdateProcessor@187] - {collection=c:buni_index_pdb_au_green, core=x:buni_index_pdb_au_green_shard1_replica_n5, node_name=n:172.18.25.11:8983_solr, replica=r:core_node6, shard=s:shard1} - [buni_index_pdb_au_green_shard1_replica_n5] webapp=/solr path=/update params={update.distrib=FROMLEADER&distrib.from=http://172.18.25.13:8983/solr/buni_index_pdb_au_green_shard1_replica_n9/&wt=javabin&version=2}{add=[0046412 (1657127463481769984)]} 0 11\n2020-01-30T04:51:40,984 - INFO [commitScheduler-87-thread-1:solr.search.SolrIndexSearcher@242] - {} - Opening [Searcher@36b45547[buni_index_pdb_au_green_shard1_replica_n5] main]\n2020-01-30T04:51:41,000 - ERROR [commitScheduler-87-thread-1:solr.common.SolrException@159] - {} - auto commit error...:org.apache.solr.common.SolrException: Error opening new searcher\n at org.apache.solr.core.SolrCore.openNewSearcher(SolrCore.java:2137)\n at org.apache.solr.core.SolrCore.getSearcher(SolrCore.java:2257)\n at org.apache.solr.update.DirectUpdateHandler2.commit(DirectUpdateHandler2.java:699)\n at org.apache.solr.update.CommitTracker.run(CommitTracker.java:270)\n at java.util.concurrent.Executors$RunnableAdapter.call(Executors.java:511)\n at java.util.concurrent.FutureTask.run(FutureTask.java:266)\n at java.util.concurrent.ScheduledThreadPoolExecutor$ScheduledFutureTask.access$201(ScheduledThreadPoolExecutor.java:180)\n at java.util.concurrent.ScheduledThreadPoolExecutor$ScheduledFutureTask.run(ScheduledThreadPoolExecutor.java:293)\n at java.util.concurrent.ThreadPoolExecutor.runWorker(ThreadPoolExecutor.java:1149)\n at java.util.concurrent.ThreadPoolExecutor$Worker.run(ThreadPoolExecutor.java:624)\n at java.lang.Thread.run(Thread.java:748)\nCaused by: org.apache.solr.common.SolrException: openNewSearcher called on closed core\n at org.apache.solr.core.SolrCore.openNewSearcher(SolrCore.java:2124)\n ... 10 more\n2020-01-30T04:51:41,018 - INFO [qtp1127224355-3628:solr.servlet.HttpSolrCall@720] - {node_name=n:172.18.25.11:8983_solr} - [admin] webapp=null path=/admin/collections params={json.nl=map&indent=true&name=buni_index_pdb_au_green&action=RELOAD&wt=json} status=0 QTime=2527\n2020-01-30T04:51:41,022 - INFO [qtp1127224355-3607:solr.core.SolrCore@2556] - {collection=c:buni_index_pdb_au_green, core=x:buni_index_pdb_au_green_shard1_replica_n5, node_name=n:172.18.25.11:8983_solr, replica=r:core_node6, shard=s:shard1} - [buni_index_pdb_au_green_shard1_replica_n5] webapp=/solr path=/select params={q={!lucene}*:*&distrib=true&json.nl=map&indent=true&rows=0&wt=json} hits=112784 status=0 QTime=2\n2020-01-30T04:51:41,024 - INFO [qtp1127224355-3628:solr.core.SolrCore@2556] - {collection=c:buni_index_pdb_au_green, core=x:buni_index_pdb_au_green_shard1_replica_n5, node_name=n:172.18.2\n\n\n\nHigh level code for Status check\nTestLoadBalancerBleed(context);\n ConfigureNoCachingHeaders(context);\n TestSitecoreRootItemExists();\n TestSitecoreSearch(_searchTerm); // Check search is working\n TestSitecoreSearch(_productSearchTerm); // Check product search is working\n TestSitecoreSearch(_diySearchTerm); // Check diy search is working\n TestSitecoreSearch(_serviceSearchTerm); // Check service search is working\n await TestAutoSuggestionsAsync(); // Check if Auto Suggestion returns any results\n TestProductCategoryNavigationModule();\n TestSolrIsRunning(); // Check if solr is running\n\nThanks\nYogesh ----",
                "case.createdDate": "2020-01-31T19:48:19Z",
                "case.caseId": "2389",
                "case.agentName": "Rohit Singh"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-2020",
            "score": 1.5099466,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Fusion Cluster HealthCheck",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/2020",
                "document.description": "original_ticket_id_5002E00001PygCvQAJ\n\nValidate Footlocker Fusion cluster installation and recommend changes to the cluster.\n\nAlso, suggest list of APIs to monitor as we are approaching Holiday seasons, and we want to ensure Fusion cluster is HA and to support 3x 190 qps. ----",
                "case.createdDate": "2019-09-05T19:12:00Z",
                "case.caseId": "2020",
                "case.agentName": "Gitanjali Palwe"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-3296",
            "score": 1.508359,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Discussion related to Fusion on-boarding",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/3296",
                "document.description": "original_ticket_id_5002E00001J2zv5QAB\n\nWe have started evaluating Fusion 4.1 and observed many qualms. We would like to discuss, so please provide a suitable time as well as technical person who will help us resolving our queries. ----",
                "case.createdDate": "2019-02-28T10:42:01Z",
                "case.caseId": "3296",
                "case.agentName": "Data Migration Agent"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-1995",
            "score": 1.4939281,
            "datasourceLabels": [],
            "fields": {
                "document.title": "“{05/2019}  Health Check Request",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/1995",
                "document.description": "original_ticket_id_5002E00001NR7P6QAL\n\nGet the environment reviewed and to get recommendations to optimize the usage ----",
                "case.createdDate": "2019-05-30T20:03:39Z",
                "case.caseId": "1995",
                "case.agentName": "Mark Bennett"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-2503",
            "score": 1.4898478,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Scheduling for Health Check",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/2503",
                "document.description": "original_ticket_id_5002E00001Pf5HsQAJ\n\nHi,\n\nWe would like to ask for Health Check to our production servers.\nCan we arrange that? What are the things we need to prepare?\n\nThank you.\n\nRegards,\nCalvin ----",
                "case.createdDate": "2019-08-28T05:58:28Z",
                "case.caseId": "2503",
                "case.agentName": "Data Migration Agent"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-1668",
            "score": 1.4855239,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Health-check Artifacts Question",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/1668",
                "document.description": "original_ticket_id_5002E00001JMkNaQAL\n\nI'm following the directions on https://support.lucidworks.com/s/article/How-to-Request-a-Health-Check to request a health-check, but I'm confused about #10.\n\n$FUSION_HOME/apps/solr-dist/server/scripts/cloud-scripts/zkcli.sh -cmd downconfig -confname -z -confdir ~/Desktop/conf\n\nI'm not sure what are the values for and , can you please explain and give an example? ----",
                "case.createdDate": "2019-03-07T18:17:29Z",
                "case.caseId": "1668",
                "case.agentName": "Gitanjali Palwe"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-6296",
            "score": 1.4845871,
            "datasourceLabels": [],
            "fields": {
                "document.title": "10/2020 Health Check Request",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/6296",
                "document.description": "Hello,\r\nWe would like to perform a health check because the last month we had fusion outages and zookeepers corruptions.\r\nAttaching the requested artifacts to the ticket. \r\nI couldn't get artifact 10 because I had an error about the config directory not existing.\r\n\r\nThank you in advance.\r\n\r\nBest Regards,\r\nMati.\r\n\r\nThis ticket includes a secure attachment. Use this link to access the attached files:\r\n https://send.lucidworks.com/receive/?thread=YAEW-XTPV&packageCode=V0K0pvW6yH7ID0b1FZdwL9n2nlGSGL0z4jckQAf2oFo#keyCode=1YFm5XW5U0KwtUsD6-UcI66pWLUjyCtxjuAVofUz8E4",
                "case.createdDate": "2020-10-05T21:30:51Z",
                "case.caseId": "6296",
                "case.agentName": "Rakesh Maski"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-10386",
            "score": 1.4782257,
            "datasourceLabels": [],
            "fields": {
                "case.createdDate": "2021-06-04T05:18:39Z",
                "document.title": "[IST] Health Check Request ",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/10386",
                "case.caseId": "10386",
                "document.description": "Hi,  \r\nplease help us to perform Health check for below points\r\n\r\n1. Problems we faced with Lenovo FUSION:\r\n\r\n•\tSpark nodes not coming up \r\n•\tHigh resource usage of Classic rest-service \r\n•\tnot able to start the jobs \r\n•\tPods were in pending state \r\n•\tSolr Shards went down "
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-2971",
            "score": 1.4764786,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Perform system Health Check",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/2971",
                "document.description": "original_ticket_id_5002E00001Ni19pQAB\n\nWe would like to perform a system health check for the dev and prod environments of OReilly.com.\n\nThey are having various issues including clusters collapsing intermittently. ----",
                "case.createdDate": "2019-06-13T19:00:01Z",
                "case.caseId": "2971",
                "case.agentName": "Gitanjali Palwe"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-2742",
            "score": 1.4741821,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Information about to know how to get the failure alerts from the services ( Connectors, admin-ui, Proxy,..) from Fusion.",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/2742",
                "document.description": "original_ticket_id_5002E00001UMr70QAD\n\nIf any service (Connectors, Proxy, Admin-ui, Web apps,...) fails in the Fusion, How to get the alerts for failure Services. ----",
                "case.createdDate": "2019-12-16T08:32:29Z",
                "case.caseId": "2742",
                "case.agentName": "Vrinda Davda"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-863",
            "score": 1.4737368,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Schedule a health check for Solr Global Portal cluster",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/863",
                "document.description": "original_ticket_id_5002E00001LGrBRQA1\n\nWe would like to go for a health check for Solr Global Portal cluster. Please contact us and let us know how shall we proceed with it. ----",
                "case.createdDate": "2019-04-25T11:35:14Z",
                "case.caseId": "863",
                "case.agentName": "Mark Bennett"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-4056",
            "score": 1.4672357,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Oct 2019 Health Check Request",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/4056",
                "document.description": "original_ticket_id_5002E00001REewpQAD\n\nHi. Just raising a ticket to have our Solr setup and environment audited. Please let us know what is the process and the information required to proceed. ----",
                "case.createdDate": "2019-10-18T16:17:38Z",
                "case.caseId": "4056",
                "case.agentName": "Gitanjali Palwe"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-896",
            "score": 1.4661298,
            "datasourceLabels": [],
            "fields": {
                "document.title": "CVE-2017-12629",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/896",
                "document.description": "original_ticket_id_5002E00001JpPCCQA3\n\nhttps://support.lucidworks.com/s/article/Lucidworks-security-advisory-for-Fusion-3?t=1553850453599\n\nCan you confirm that this issue has been fixed in Fusion 4 and none of the steps above are required to be made?\n\nThanks\n\nGordon ----",
                "case.createdDate": "2019-03-29T09:11:00Z",
                "case.caseId": "896",
                "case.agentName": "Rohit Singh"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-6102",
            "score": 1.4659947,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Amazon EKS ALB health check issue of Fusion 5.2.0 for Market Kurly",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/6102",
                "document.description": "Market Kurly is currently planning to upgrade from Fusion 4 to Fusion 5. \r\nWhile waiting for Fusion 5.2.1 to be released, we tested 5.1.4 and 5.2.0 for Kubernetes environment testing.\r\nWorker Node Health Status of EKS ALB, which was not a problem in version 5.1.4, occurred in 5.2.0.\r\nDetailed symptoms are summarized in the attached file.\r\nThis customer wants this issue to be resolved quickly.\r\n\r\nMy name is Roy Seong working at TOBESOFT which is a Master Distributor of Lucidworks in South Korea.\r\nI open this support case on behalf of Market Kurly.\r\n\r\nif you need any other information please feel free request us\r\n\r\n\r\nThis ticket includes a secure attachment. Use this link to access the attached files:\r\n https://send.lucidworks.com/receive/?thread=ZK80-9TWJ&packageCode=8TnSx69r784GDKRdZF00dp4UoK9L4vGda7JTn4fdJZ8#keyCode=91LnUF-pfxRuS7_NI6ii7cMoSQ3p65Y7011amDDa6Vg",
                "case.createdDate": "2020-09-16T13:25:57Z",
                "case.caseId": "6102",
                "case.agentName": "Vrinda Davda"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-6350",
            "score": 1.4643342,
            "datasourceLabels": [],
            "fields": {
                "document.title": "TDA Health check 2020",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/6350",
                "document.description": "This is a healthcheck case that should be assigned to Greg Harris\r\n\r\nGreg has directed us on gathering proper data for the health check. I'm setting up the ticket so we can add data to the case for Greg to access. \r\n\r\nThanks, \r\n\r\nCharles, ",
                "case.createdDate": "2020-10-09T16:06:51Z",
                "case.caseId": "6350",
                "case.agentName": "Greg Harris"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-4605",
            "score": 1.4635454,
            "datasourceLabels": [],
            "fields": {
                "document.title": "ZK in PROD returns 503 during Healthcheck",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/4605",
                "document.description": "original_ticket_id_5002E00001bGCS9QAO\n\nWe have 3 ZK nodes in our PROD cluster, and the first one and last one returned 503 error during the check out\n\nhttp://lwzkprdvh01:8091/services/zookeeper/healthCheck (Returns 503)\nhttp://lwzkprdvh01:8091/services/zookeeper/healthCheck (Returns 200)\nhttp://lwzkprdvh01:8091/services/zookeeper/healthCheck (Returns 503)\n\nRunning the fusion status command indicates that ZK is starting on 01 and 03 but still stuck there. ----",
                "case.createdDate": "2020-06-04T15:37:09Z",
                "case.caseId": "4605",
                "case.agentName": "Data Migration Agent"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-310",
            "score": 1.459369,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Lowes.ca  - Site Issues - Search is down",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/310",
                "document.description": "original_ticket_id_5002E00001ZIDdsQAH\n\nLowes.ca site is experiencing slowness. We need immediate support from Lucidworks. ----",
                "case.createdDate": "2020-04-23T12:53:05Z",
                "case.caseId": "310",
                "case.agentName": "Shubhashree  Rudramunaiah"
            }
        }, {
            "type": "case",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-CASE-2205",
            "score": 1.4591058,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Fusion 3 to Fusion 5 Upgrade",
                "document.url": "https://lucidworkssupport.zendesk.com/agent/tickets/2205",
                "document.description": "original_ticket_id_5002E00001X3RB1QAN\n\nHi,\n\nWe are currently using Fusion 3 version, We wants to make a upgrade. Between we wants to know the risk, benifits of upgarading to Fusion 5 by skiping the Fusion 4.\n\nWe would like to have a small call to know more.\n\nPlease let us know if you need any information.\n\nThanks,\nSushil ----",
                "case.createdDate": "2020-02-10T07:21:50Z",
                "case.caseId": "2205",
                "case.agentName": "Rohit Singh"
            }
        }]
};
const similarArticles = {
    "queryId": "754ac285-cd64-41d7-bdd7-70b21b718019",
    "hits": 40,
    "docs": [{
            "type": "knowledgeArticle",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-KNOWLEDGEARTICLE-360049891273",
            "score": 1.5457029,
            "datasourceLabels": [],
            "fields": {
                "document.title": "How to Request a Health Check",
                "document.url": "https://support.lucidworks.com/hc/en-us/articles/360049891273-How-to-Request-a-Health-Check",
                "knowledgeArticle.articleId": "360049891273"
            }
        }, {
            "type": "knowledgeArticle",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-KNOWLEDGEARTICLE-360052984674",
            "score": 1.3934914,
            "datasourceLabels": [],
            "fields": {
                "document.title": "How to Submit a Managed Fusion promotion request for a Production environment",
                "document.url": "https://support.lucidworks.com/hc/en-us/articles/360052984674-How-to-Submit-a-Managed-Fusion-promotion-request-for-a-Production-environment",
                "knowledgeArticle.articleId": "360052984674"
            }
        }, {
            "type": "knowledgeArticle",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-KNOWLEDGEARTICLE-360051544393",
            "score": 1.3629186,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Fusion Server Licensing",
                "document.url": "https://support.lucidworks.com/hc/en-us/articles/360051544393-Fusion-Server-Licensing-",
                "knowledgeArticle.articleId": "360051544393"
            }
        }, {
            "type": "knowledgeArticle",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-KNOWLEDGEARTICLE-360051501793",
            "score": 1.353777,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Pre-Production Checklist",
                "document.url": "https://support.lucidworks.com/hc/en-us/articles/360051501793-Pre-Production-Checklist",
                "knowledgeArticle.articleId": "360051501793"
            }
        }, {
            "type": "knowledgeArticle",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-KNOWLEDGEARTICLE-360051502433",
            "score": 1.3517853,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Fusion Logs Maintenance",
                "document.url": "https://support.lucidworks.com/hc/en-us/articles/360051502433-Fusion-Logs-Maintenance",
                "knowledgeArticle.articleId": "360051502433"
            }
        }, {
            "type": "knowledgeArticle",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-KNOWLEDGEARTICLE-360050740834",
            "score": 1.3067467,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Fusion-Ground Truth Job",
                "document.url": "https://support.lucidworks.com/hc/en-us/articles/360050740834-Fusion-Ground-Truth-Job",
                "knowledgeArticle.articleId": "360050740834"
            }
        }, {
            "type": "knowledgeArticle",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-KNOWLEDGEARTICLE-360052117234",
            "score": 1.2931563,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Solr and Fusion Query Load Testing",
                "document.url": "https://support.lucidworks.com/hc/en-us/articles/360052117234-Solr-and-Fusion-Query-Load-Testing",
                "knowledgeArticle.articleId": "360052117234"
            }
        }, {
            "type": "knowledgeArticle",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-KNOWLEDGEARTICLE-360050769534",
            "score": 1.2822931,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Predictive Merchandiser Licensing",
                "document.url": "https://support.lucidworks.com/hc/en-us/articles/360050769534-Predictive-Merchandiser-Licensing",
                "knowledgeArticle.articleId": "360050769534"
            }
        }, {
            "type": "knowledgeArticle",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-KNOWLEDGEARTICLE-360057497133",
            "score": 1.2733583,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Port scanning causes Ignite issues in 4.x versions of Fusion",
                "document.url": "https://support.lucidworks.com/hc/en-us/articles/360057497133-Port-scanning-causes-Ignite-issues-in-4-x-versions-of-Fusion",
                "knowledgeArticle.articleId": "360057497133"
            }
        }, {
            "type": "knowledgeArticle",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-KNOWLEDGEARTICLE-360049851193",
            "score": 1.2648172,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Autocomplete using Fusion Signals: two ways-NEEDS UPDATING",
                "document.url": "https://support.lucidworks.com/hc/en-us/articles/360049851193-Autocomplete-using-Fusion-Signals-two-ways-NEEDS-UPDATING",
                "knowledgeArticle.articleId": "360049851193"
            }
        }, {
            "type": "knowledgeArticle",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-KNOWLEDGEARTICLE-360049122354",
            "score": 1.258763,
            "datasourceLabels": [],
            "fields": {
                "document.title": "How to force Fusion's UI to only use HTTPS",
                "document.url": "https://support.lucidworks.com/hc/en-us/articles/360049122354-How-to-force-Fusion-s-UI-to-only-use-HTTPS",
                "knowledgeArticle.articleId": "360049122354"
            }
        }, {
            "type": "knowledgeArticle",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-KNOWLEDGEARTICLE-360049890933",
            "score": 1.2546461,
            "datasourceLabels": [],
            "fields": {
                "document.title": "How to access Fusion datasources, pipelines, Solr collection configs via Zookeeper",
                "document.url": "https://support.lucidworks.com/hc/en-us/articles/360049890933-How-to-access-Fusion-datasources-pipelines-Solr-collection-configs-via-Zookeeper",
                "knowledgeArticle.articleId": "360049890933"
            }
        }, {
            "type": "knowledgeArticle",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-KNOWLEDGEARTICLE-360049686973",
            "score": 1.2516468,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Using the Fusion 4.0 Object Explorer",
                "document.url": "https://support.lucidworks.com/hc/en-us/articles/360049686973-Using-the-Fusion-4-0-Object-Explorer",
                "knowledgeArticle.articleId": "360049686973"
            }
        }, {
            "type": "knowledgeArticle",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-KNOWLEDGEARTICLE-360055673814",
            "score": 1.2464054,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Backing up your Fusion collections along with Signals and Aggregations",
                "document.url": "https://support.lucidworks.com/hc/en-us/articles/360055673814-Backing-up-your-Fusion-collections-along-with-Signals-and-Aggregations",
                "knowledgeArticle.articleId": "360055673814"
            }
        }, {
            "type": "knowledgeArticle",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-KNOWLEDGEARTICLE-360051480073",
            "score": 1.240435,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Requesting a Designated User Account-CE Team",
                "document.url": "https://support.lucidworks.com/hc/en-us/articles/360051480073-Requesting-a-Designated-User-Account-CE-Team",
                "knowledgeArticle.articleId": "360051480073"
            }
        }, {
            "type": "knowledgeArticle",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-KNOWLEDGEARTICLE-360049071934",
            "score": 1.2391384,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Adjusting Logging Levels in Fusion",
                "document.url": "https://support.lucidworks.com/hc/en-us/articles/360049071934-Adjusting-Logging-Levels-in-Fusion",
                "knowledgeArticle.articleId": "360049071934"
            }
        }, {
            "type": "knowledgeArticle",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-KNOWLEDGEARTICLE-360048925174",
            "score": 1.2391384,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Adjusting Logging Levels in Fusion",
                "document.url": "https://support.lucidworks.com/hc/en-us/articles/360048925174-Adjusting-Logging-Levels-in-Fusion",
                "knowledgeArticle.articleId": "360048925174"
            }
        }, {
            "type": "knowledgeArticle",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-KNOWLEDGEARTICLE-360050733634",
            "score": 1.2361337,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Knowing your Zookeeper and checking on it’s health",
                "document.url": "https://support.lucidworks.com/hc/en-us/articles/360050733634-Knowing-your-Zookeeper-and-checking-on-it-s-health",
                "knowledgeArticle.articleId": "360050733634"
            }
        }, {
            "type": "knowledgeArticle",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-KNOWLEDGEARTICLE-360051560153",
            "score": 1.2256035,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Restrict User Access to only Analytics in Fusion",
                "document.url": "https://support.lucidworks.com/hc/en-us/articles/360051560153-Restrict-User-Access-to-only-Analytics-in-Fusion",
                "knowledgeArticle.articleId": "360051560153"
            }
        }, {
            "type": "knowledgeArticle",
            "id": "2e346748-1a2a-43d5-8616-2cdbb40af45d-KNOWLEDGEARTICLE-360059123573",
            "score": 1.2227261,
            "datasourceLabels": [],
            "fields": {
                "document.title": "Issue Solr Admin Commands via Fusion API Endpoints",
                "document.url": "https://support.lucidworks.com/hc/en-us/articles/360059123573-Issue-Solr-Admin-Commands-via-Fusion-API-Endpoints",
                "knowledgeArticle.articleId": "360059123573"
            }
        }]
};
const experts = similarCases;

const getMockApiData = (type) => {
    const typeDataMap = {
        search: agentSearchData,
        similarCases: similarCases,
        similarArticles: similarArticles,
        sme: experts,
    };
    return typeDataMap[type] || queryZoneData;
};
const searchResultsConfig = {
    facets: ['file.extension'],
    rows: 20,
};
const sortList = (docs, sortParam) => {
    if (lodash.includes(['oldest-published', 'newest-published'], sortParam)) {
        docs = lodash.orderBy(docs, ['fields.document.sourceCreateTimestamp'], [sortParam === 'oldest-published' ? 'asc' : 'desc']);
    }
    return docs;
};
const getMockData = ({ type, params }) => {
    const query = params.query === '*:*' ? '' : params.query;
    const { rows } = searchResultsConfig;
    const page = params.page || 0;
    let rowDocs = getMockApiData(type).docs;
    if (query) {
        rowDocs = rowDocs.filter((doc) => lodash.toLower(doc.fields['document.title']).includes(lodash.toLower(query)));
    }
    const docs = lodash.chain(sortList(rowDocs, params.sort))
        .cloneDeep()
        .map((doc) => (Object.assign(Object.assign({}, doc), { fields: Object.assign(Object.assign({}, doc.fields), { 'document.description': lodash.replace(doc.fields['document.description'], new RegExp(`(${query})`, 'gi'), '<em>$1</em>') }) })))
        .value();
    let pagination;
    switch (type) {
        case 'detail':
            return lodash.find(getMockApiData(type).docs, { id: params.id });
        case 'search':
        case 'similarCases':
        case 'similarArticles':
        case 'sme':
            pagination = {
                page,
                start: page * rows,
                end: Math.min((page + 1) * rows - 1, docs.length),
            };
            return {
                hits: docs.length,
                docs: docs.slice(pagination.start, pagination.end + 1),
            };
    }
    return {};
};

class QueryServiceStoreMockPlugin extends DataStoreMockPlugin {
    constructor() {
        super(...arguments);
        this.mockApis = [...DataStoreMockPlugin.crudMockApis, 'doRequest'];
    }
    /**
     * @override
     */
    async getMockData(type = this.store.queryType, params = this.store.currentParams) {
        return Promise.resolve(getMockData({ type, params }));
    }
    async doRequest() {
        return this.getMockData();
    }
}

class QueryServiceStore extends MoBaseStore {
    constructor(config) {
        var _a;
        var _b;
        super([], Object.assign({ 
            // It should notify with the same zone store references for components to subscribe to.
            cloneNotificationPayload: false, requestDebounceWait: 100, mockPlugin: QueryServiceStoreMockPlugin }, config));
        this.data = [];
        this.initLoad = false;
        this.loaded = false;
        this.mockPluginApplied = false;
        this.requestParamsByType = {
            search: ['query', 'sort', 'filters', 'page', 'utcOffset', 'fieldList', 'facets', 'sessionId', 'limit', 'highlight'],
            searchahead: ['query', 'sort', 'filters', 'page', 'utcOffset', 'fieldList', 'sessionId', 'limit', 'highlight'],
            similarCases: ['query', 'sort', 'filters', 'page', 'utcOffset', 'fieldList', 'sessionId', 'limit', 'highlight'],
            similarArticles: ['query', 'sort', 'filters', 'page', 'utcOffset', 'fieldList', 'sessionId', 'limit', 'highlight'],
            sme: ['query', 'sort', 'filters', 'page', 'utcOffset', 'fieldList', 'sessionId', 'limit', 'highlight'],
        };
        this.getApiService = () => getStoreApiService();
        this.queryType = config.type;
        this.csAgent = config.csAgent;
        this.doRequest = dist(this.doRequest, this.config.requestDebounceWait);
        this.currentParams = lodash.includes(['search', 'searchahead'], this.queryType) ? { query: '*:*' } : {};
        this.currentParams.utcOffset = MoDatesService.getUtcOffset();
        this.currentParams.sessionId = getOrCreateSession();
        this.currentParams.analyticsData = false;
        this.currentParams.limit = 20;
        (_a = (_b = this.config).mock) !== null && _a !== void 0 ? _a : (_b.mock = this.getApiService().meta.config.mock);
        const { mock, mockPlugin } = this.config;
        if (mock && mockPlugin && !this.mockPluginApplied) {
            const appliedMockPlugin = new mockPlugin(this);
            appliedMockPlugin.applyMocks();
            this.mockPluginApplied = true;
        }
    }
    /**
     * @override
     */
    getMeta() {
        return this.cloneProps(['config', 'data', 'currentParams']);
    }
    /**
     * @override
     */
    subscribe(callback, component) {
        const unSubscribe = super.subscribe(callback, component);
        if (!this.initLoad) {
            this.initLoad = true;
        }
        return unSubscribe;
    }
    setProbe(doc) {
        if (!lodash.includes(['search', 'searchahead'], this.queryType)) {
            this.currentParams = lodash.omit(this.currentParams, 'query');
            this.currentParams.probe = {
                title: lodash.get(doc.fields, 'document.title'),
                description: lodash.get(doc.fields, 'document.description'),
                caseId: lodash.get(doc.fields, 'case.caseId')
            };
        }
    }
    setQueryType(type, doc) {
        this.queryType = type;
        if (doc) {
            this.setProbe(doc);
        }
        return this.request(this.currentParams);
    }
    setQuery(query, type) {
        this.queryType = type || this.queryType;
        return this.request({ query });
    }
    setFacets(filters) {
        return this.request({ filters });
    }
    setPage(page) {
        return this.request({ page });
    }
    setSort(sortField, sortOrder) {
        return this.request({ sort: [{ sortField, sortOrder }] });
    }
    async getDocDetail(docId) {
        return this.request({ id: docId });
    }
    getCsAgent() {
        return this.csAgent;
    }
    async request(params, updateIndex) {
        let response;
        this.setActionParams(params);
        if (params.query !== "*:*") {
            response = await this.doRequest();
        }
        else {
            response = { docs: [], queryId: "1" };
        }
        this.setZoneStores(response || {}, updateIndex);
        this.loaded = true;
        this.notifyUpdates();
        return response;
    }
    /**
     * @override
     */
    notifyUpdatesOnSubscribe(callback) {
        if (this.loaded) {
            super.notifyUpdatesOnSubscribe(callback);
        }
    }
    async doRequest() {
        if (this.queryType == 'similarArticles') {
            const currentParams = Object.assign(Object.assign({}, this.currentParams), { probe: lodash.omit(Object.assign({}, this.currentParams.probe), 'caseId') });
            return (this.getApiService().invoke('agent-service', this.queryType, { data: currentParams }) || {});
        }
        return (this.getApiService().invoke('agent-service', this.queryType, { data: this.currentParams }) || {});
    }
    setActionParams(params) {
        const supportedParams = this.requestParamsByType[this.queryType];
        const newParams = lodash.pick(params, supportedParams);
        // There will be at most one facet per store action.
        const targetFacet = lodash.first(newParams.filters);
        if (targetFacet) {
            // This also un-toggles the existing facet value from `this.currentParams.filters`.
            const filterToRemove = {
                field: targetFacet.field,
                values: lodash.isArray(targetFacet.values) ? targetFacet.values : [targetFacet.values]
            };
            const isDeselect = !lodash(this.currentParams.filters).remove(filterToRemove).isEmpty();
            // Remove all existing facet values for the same facet as render API only supports ANDs within facets.
            lodash.remove(this.currentParams.filters, { field: targetFacet.field });
            if (!isDeselect) {
                this.currentParams.filters = [...(this.currentParams.filters || []), targetFacet];
            }
        }
        else {
            // Overwrite current params on top level, this also clears all existing filters with params `{ filters: [] }`.
            this.currentParams = Object.assign(Object.assign({}, this.currentParams), newParams);
        }
        // Reset to the first page when changing query or filters.
        if (lodash.intersection(['query', 'filters'], lodash.keys(params)).length) {
            this.currentParams.page = 0;
        }
    }
    setZoneStores(response, updateIndex) {
        const zones = [];
        const rows = this.currentParams.limit || 20;
        const start = rows * (this.currentParams.page || 0);
        const end = start + rows - 1;
        const pagination = { start, end };
        let docs = response.docs;
        let hits = response.hits;
        if (this.queryType === 'sme') {
            const agentName = 'case.agentName';
            // Sort agents by rank asc
            docs = lodash.chain(docs)
                .filter(doc => !lodash.isEmpty(doc.fields[agentName]))
                .groupBy(doc => doc.fields[agentName])
                .map((docs, _agentName) => lodash.first(lodash.orderBy(docs, 'rank', 'asc')))
                .orderBy('rank', 'asc')
                .map(doc => (Object.assign(Object.assign({}, doc), { type: 'expert', fields: { 'document.title': doc.fields[agentName] } })))
                .value();
            hits = lodash.size(docs);
        }
        zones.push({ docs, hits, zoneType: this.queryType === 'searchahead' ? 'typeahead' : 'results-list', pagination });
        if (lodash.isEmpty(zones)) {
            this.data = [];
        }
        else {
            lodash.each(zones, (zone, index) => {
                if (!this.data[index]) {
                    this.data[index] = new ZoneStore(zone, {
                        index,
                        params: this.currentParams,
                        onAction: lodash.bind(this.onZoneAction, this),
                    });
                }
                else if (lodash.isUndefined(updateIndex) || updateIndex === index) {
                    this.data[index].setData(zone, lodash.cloneDeep(this.currentParams));
                }
            });
        }
    }
    /**
     * Only refresh the zone if the action is specific to the zone itself, or do global refresh.
     */
    onZoneAction(meta) {
        var _a;
        if (meta === null || meta === void 0 ? void 0 : meta.filters) {
            lodash.each(meta.filters, (filter) => {
                if (!lodash.isEmpty(filter.value) && lodash.isEmpty(filter.values)) {
                    filter.values = lodash.flatten([filter.value]);
                    delete filter.value;
                }
            });
        }
        const noneGlobalActions = ['sort', 'page'];
        const zoneToUpdate = !lodash(meta).pick(noneGlobalActions).isEmpty() && meta.store;
        const indexToUpdate = (_a = zoneToUpdate.meta) === null || _a === void 0 ? void 0 : _a.config.index;
        if (lodash.isString(meta === null || meta === void 0 ? void 0 : meta.sort)) {
            const field = meta === null || meta === void 0 ? void 0 : meta.sort;
            const createdField = 'document.sourceCreateTimestamp';
            const sortField = getSortFieldMap()[field] || createdField;
            const sortOrder = lodash.get(getSortOrderMap(), field, 'desc');
            meta = Object.assign(Object.assign({}, meta), { sort: [{ sortField, sortOrder }] });
        }
        this.request(meta, indexToUpdate);
    }
}

const zoneComponentTags = {
    'facets': 'mo-zone-facets',
    'resultsList': 'mo-zone-results',
    'typeahead': 'mo-zone-doc-list',
    'detail': 'mo-zone-doc-details',
    'analytics': 'mo-analytics-zone',
    'relatedContent': 'mo-zone-results',
};
const analyticsZoneComponentTags = {
    'time-series': 'mo-chart',
    'donut': 'mo-data-card',
    'stat': 'mo-data-card',
    'bar': 'mo-data-card',
};
const getZoneComponentTag = (zoneType) => {
    return { tag: zoneComponentTags[zoneType] };
};
const getAnalyticsZoneComponentTag = (metricType) => {
    return { tag: analyticsZoneComponentTags[metricType] };
};
const getRenderStore = (config) => new RenderStore(config);
const getQueryStore = (config) => new QueryStore(config);
const getQueryServiceStore = (config) => new QueryServiceStore(config);

const zoneComponentUtils = /*#__PURE__*/Object.freeze({
    __proto__: null,
    getZoneComponentTag: getZoneComponentTag,
    getAnalyticsZoneComponentTag: getAnalyticsZoneComponentTag,
    getRenderStore: getRenderStore,
    getQueryStore: getQueryStore,
    getQueryServiceStore: getQueryServiceStore
});

export { ApiService as A, QueryStore as Q, RenderStore as R, ZoneStore as Z, setStoreApiService as a, buildIntrospectApi as b, storeHelpers as c, dist as d, QueryServiceStore as e, agentSearchData as f, getStoreApiService as g, getAnalyticsZoneComponentTag as h, invokeService as i, getZoneComponentTag as j, makeFetchRequest as m, renderStoreMockHelpers as r, setStoreApiServiceConfig as s, zoneComponentUtils as z };
